<script setup>
import { ref, computed, onMounted, watch, h, nextTick, provide } from 'vue';
import ConsultantVersionCompactView from '@/components/consultation/ConsultantVersionCompactView.vue';
import ConsultationConclusion from '@/views/consultation-workspace/ConsultationConclusion.vue';
import VersionItemViewModal from '@/components/consultation/VersionItemViewModal.vue';
import AdminFlatBalanceSheet from '@/components/finance-version/AdminFlatBalanceSheet.vue';
import WhiteboardPhotoPanel from '@/components/finance-version/WhiteboardPhotoPanel.vue';
import FinanceStatusBanner from '@/components/finance-version/FinanceStatusBanner.vue';
import AdminEditorProfileCard from '@/components/finance-version/AdminEditorProfileCard.vue';
import AdminEditorFooter from '@/components/finance-version/AdminEditorFooter.vue';
import { useRoute, useRouter } from 'vue-router';
import { message, Modal } from 'ant-design-vue';
import { CheckCircleOutlined, ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons-vue';
import { assertSuccessfulSheetResponse, buildTypeSavePayload } from '@/utils/flatBalanceSheetEditor';
import { usePermission } from '@/composables/usePermission';
import formatDateTime from '@/utils/formatDateTime';
import {
    buildConsultationVersionEditLocation,
    buildVersionWorkflowSteps,
    isVersionReadonly,
    resolveConsultationWorkspaceVersionId,
    buildConsultationConclusionLocation,
    shouldShowVersionWorkflow
} from '@/utils/versionWorkflow';

// API
import { getVersion } from '@/api/finance-version/getVersion';
import { createVersion } from '@/api/finance-version/createVersion';
import { copyVersion } from '@/api/finance-version/copyVersion';
import { updateVersion } from '@/api/finance-version/updateVersion';
import { getVersionSummary } from '@/api/finance-version/getVersionSummary';
import { finishVersion, saveVersionWorkspace } from '@/api/finance-version/finishVersion';
import { exportVersion } from '@/api/finance-version/exportVersion';
import { userList } from '@/api/admin/userList';
import { getFinanceVersions } from '@/api/finance-version/versionList';
// 移除 missing_entries 功能
import { updateAssetLiability } from '@/api/finance/assetLiability';
import { updateUserDetailVersion } from '@/api/user-detail-version/updateUserDetailVersion';
import { getTypes } from '@/api/finance/types';
import { getOptions, getOptionsBatch } from '@/api/finance/options';
import { getFields } from '@/api/finance/fields';
import {
    completeConsultationBooking,
    getConsultationWorkspace
} from '@/api/consultation-workspace/consultationWorkspaceApi';

// 動態字段與關聯、陣列支援
import { getOptionRelations } from '@/api/finance/optionRelations';
import { getDetailStatus, getDetailStatusBatch } from '@/api/finance/detailStatus';
import { getMissingEntries } from '@/api/finance/missingEntries';

// 諮詢工作台串接：從諮詢主畫面進入時，URL 帶 ?consultation_id={id}
// → 盤點僅儲存、不結束諮詢；結束諮詢請至學員詳情頁

const route = useRoute();
const router = useRouter();
const { canExport } = usePermission('FinanceVersionList');

const isEdit = computed(() => !!route.params.id);
const balanceId = computed(() =>
    isEdit.value ? Number(route.params.id) : null
);
// 檢視路由、學員版及已完成版本唯讀；尚未完成的盤點／諮詢版可由 edit 路由續編。
// readonly query 只作為導覽提示，不作為權限來源。
const isReadonly = computed(() => {
    return isVersionReadonly({
        routeName: route.name,
        isEdit: isEdit.value,
        source: versionSource.value,
        status: versionSheetStatus.value
    });
});

/** 諮詢模式：URL 帶 ?consultation_id 表示這版本是某次諮詢的產出 */
const consultationId = computed(() => {
    const raw = Number(route.query.consultation_id || 0);
    return Number.isFinite(raw) && raw > 0 ? raw : null;
});
const isConsultationMode = computed(() => consultationId.value !== null);

/** 諮詢工作台版本編輯：一頁式快速填寫（免逐步點擊） */
const isConsultantWorkspaceRoute = computed(() => {
    const n = route.name;
    return (
        n === 'ConsultationWorkspaceVersionEdit' ||
        n === 'ConsultationWorkspaceVersionView' ||
        n === 'ConsultationWorkspaceVersionCreate'
    );
});
const isConsultantOnePage = computed(() => {
    if (!isEdit.value) return false;
    if (isConsultantWorkspaceRoute.value) return true;
    return String(route.path || '').includes('/consultation-workspace/versions/');
});
const isAdminFlatEditor = computed(
    // 所有既有版本都使用同一套攤平表格；諮詢工作台只是額外提供流程與提交操作。
    () => isEdit.value
);
const showEditStep = (step) =>
    isConsultantOnePage.value || isAdminFlatEditor.value || currentStep.value === step;
const showSummaryStep = computed(
    () => isConsultantOnePage.value || isAdminFlatEditor.value || currentStep.value === 4
);
const showConsultantCompact = computed(
    () => isConsultantOnePage.value && isEdit.value && !isAdminFlatEditor.value
);
const scrollToConsultantSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
    });
};

// 編輯模式步驟：版本資訊 / 個人資訊 / 資產 / 負債 / 總結
const editSteps = [
    { title: '版本資訊' },
    { title: '個人資訊' },
    { title: '資產' },
    { title: '負債' },
    { title: '總結' }
];
const currentStep = ref(isEdit.value ? 0 : 0);

// 新增模式（保留）
const steps = [
    { title: '選擇學員' },
    { title: '建立/複製版本' },
    { title: '資產' },
    { title: '負債' },
    { title: '總結與完成' }
];

// 新增模式：選擇學員與建立/複製（保留）
const selectedUserId = ref('');
const userKeyword = ref('');
const userOptions = ref([]);
const userLoading = ref(false);

const searchUsers = async (keyword) => {
    userLoading.value = true;
    try {
        const res = await userList(20, 1, keyword ? { name: keyword } : {});
        const list = res?.data?.list || [];
        userOptions.value = list.map((u) => ({
            value: String(u.id),
            label: u.name || `學員${u.id}`
        }));
    } catch (e) {
        console.error('searchUsers error', e);
    } finally {
        userLoading.value = false;
    }
};

watch(userKeyword, (kw) => {
    searchUsers(kw);
});

const form = ref({
    external_name: '',
    internal_name: '',
    source: 0,
    is_visible_user: true
});
const selectedCopyVersionId = ref('');
const copyVersionOptions = ref([]);
const copyLoading = ref(false);

const loadCopyOptions = async () => {
    if (!selectedUserId.value) {
        copyVersionOptions.value = [];
        return;
    }
    copyLoading.value = true;
    try {
        const res = await getFinanceVersions({
            user_id: Number(selectedUserId.value),
            page: 1,
            page_size: 50,
            source: 0
        });
        const data = (Array.isArray(res?.data) ? res.data : []).filter(
            (version) => Number(version.source) === 0
        );
        copyVersionOptions.value = data.map((v) => ({
            value: String(v.id),
            label: `${v.internal_name || v.external_name || '未命名'}（#${
                v.id
            }）`
        }));
    } catch (e) {
        console.error('loadCopyOptions error', e);
    } finally {
        copyLoading.value = false;
    }
};

watch(selectedUserId, () => {
    loadCopyOptions();
});

const creating = ref(false);
const handleCreate = async () => {
    if (!selectedUserId.value) return message.warning('請先選擇學員');
    try {
        creating.value = true;
        const payload = {
            user_id: Number(selectedUserId.value),
            external_name: form.value.external_name?.trim(),
            internal_name: form.value.internal_name?.trim(),
            source: 0,
            is_visible_user: Boolean(form.value.is_visible_user)
        };
        const res = await createVersion(payload);
        if (res?.status === 1 && res?.data?.id) {
            message.success('建立成功');
            router.replace(`/finance-version-edit/${res.data.id}`);
        } else {
            message.error(res?.message || '建立失敗');
        }
    } catch (e) {
        console.error('create version error', e);
        message.error('建立失敗');
    } finally {
        creating.value = false;
    }
};

const copying = ref(false);
const handleCopy = async () => {
    if (!selectedCopyVersionId.value)
        return message.warning('請選擇要複製的版本');
    try {
        copying.value = true;
        const res = await copyVersion(Number(selectedCopyVersionId.value), {
            external_name: form.value.external_name?.trim(),
            internal_name: form.value.internal_name?.trim(),
            source: 0,
            is_visible_user: 1
        });
        if (res?.status === 1 && res?.data?.id) {
            message.success('複製成功');
            router.replace(`/finance-version-edit/${res.data.id}`);
        } else {
            message.error(res?.message || '複製失敗');
        }
    } catch (e) {
        console.error('copy version error', e);
        message.error('複製失敗');
    } finally {
        copying.value = false;
    }
};

// 編輯模式：個人資訊編輯
const udvId = ref(null);
const udvForm = ref({ age: '', job: '', marital_status: 0, children: 0 });
const udvLoading = ref(false);

// 版本資訊表單
const versionInfoForm = ref({
    internal_name: '',
    external_name: '',
    requires_booking: false
});
const versionInfoLoading = ref(false);
const exportLoading = ref(false);
const versionWorkflow = ref(null);
const versionUserId = ref(null);
const showConclusion = ref(false);
watch(showConclusion, (value) => {
    console.log('[CW conclusion render]', { value, route: route.name, id: route.params.id });
});
const versionWorkflowSteps = computed(() =>
    buildVersionWorkflowSteps(versionWorkflow.value || {})
);
const versionWorkflowCurrent = computed(() => {
    const index = versionWorkflowSteps.value.findIndex(
        (step) => Number(step.data?.id) === Number(balanceId.value)
    );
    // conclusion_completed 代表整個 round 已完成，不代表目前正在檢視結論頁；
    // 目前版本必須永遠由 URL 的版本 ID 對應 workflow 計算。
    return Math.max(0, index);
});
/** 既有資產負債表固定顯示完整四階段流程；尚未建立的節點維持禁用。 */
const showConsultantWorkflowBanner = computed(
    () => shouldShowVersionWorkflow(versionWorkflow.value, isEdit.value)
);
const changeVersionWorkflowStep = (index) => {
    console.log('[CW workflow step change]', {
        index,
        step: versionWorkflowSteps.value[index]?.key,
        balanceId: balanceId.value,
        route: route.name,
        workflow: versionWorkflow.value
    });
    openVersionWorkflowStep(versionWorkflowSteps.value[index]);
};
const openConclusionInline = () => {
    if (isConsultantWorkspaceRoute.value) showConclusion.value = true;
};

const openVersionWorkflowStep = async (step) => {
    console.log('[CW workflow open]', {
        key: step?.key,
        balanceId: balanceId.value,
        route: route.name,
        consultationId: consultationId.value,
        userId: versionUserId.value || versionWorkflow.value?.user_id || route.query.user_id || null
    });
    if (step.key === 'conclusion') {
        const consultationVersion =
            versionWorkflow.value?.post_consultation || versionWorkflow.value?.consultation;
        const consultationCompleted =
            Boolean(consultationVersion?.locked) ||
            consultationVersion?.status === 1 ||
            consultationVersion?.status === '1' ||
            consultationVersion?.status === '完成';
        if (!consultationCompleted) {
            message.warning('盤點版與諮詢版都完成後才能查看結論');
            return;
        }
        showConclusion.value = true;
        return;
    }
    showConclusion.value = false;
    if (!step.data?.id) return;
    // 已完成版本一律檢視；只有尚未鎖定且可編輯的流程版本才進編輯。
    const openAsEdit = Boolean(step.editable) && !step.locked && !step.data?.locked;
    const viewRoute = isConsultantWorkspaceRoute.value
        ? openAsEdit
            ? 'ConsultationWorkspaceVersionEdit'
            : 'ConsultationWorkspaceVersionView'
        : openAsEdit
          ? 'FinanceVersionEdit'
          : 'FinanceVersionView';
    router.push({
        name: viewRoute,
        params: { id: step.data.id },
        query: {
            ...(consultationId.value ? { consultation_id: consultationId.value } : {}),
            ...(versionUserId.value || route.query.user_id
                ? { user_id: versionUserId.value || route.query.user_id }
                : {})
        }
    });
};

const parseVersionPayload = (res) => {
    const raw = res?.data;
    const dataBlock =
        raw?.data?.original?.data ?? raw?.original?.data ?? raw?.data ?? raw;
    return dataBlock || {};
};

// 不同版本的摘要 API 可能回傳 flat rows 或依資產／負債、類型分組的資料；
// 編輯器統一攤平成列資料，避免後端已有資料卻被當成空表。
const normalizeTypeData = (value) => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== 'object') return [];
    const rows = [];
    const visit = (node) => {
        if (Array.isArray(node)) {
            node.forEach(visit);
            return;
        }
        if (!node || typeof node !== 'object') return;
        if (node.data_id != null && node.type_id != null) {
            rows.push(node);
            return;
        }
        Object.values(node).forEach(visit);
    };
    visit(value);
    return rows;
};

const loadVersionForEdit = async () => {
    if (!isEdit.value) return;
    try {
        const res = await getVersion(balanceId.value);
        const dataBlock = parseVersionPayload(res);
        const ud = dataBlock?.user_detail || {};
        const idCandidate =
            ud.user_detail_id ?? ud.id ?? dataBlock?.user_detail_id;
        udvId.value = idCandidate || null;
        udvForm.value = {
            age: Number(ud.age ?? 0),
            job: ud.job ?? '',
            marital_status: Number(ud.marital_status ?? 0),
            children: Number(ud.children ?? 0)
        };

        // 儲存原始資料快照
        saveOriginalUdvData();
        // 解析 type_data 供列表使用
        typeData.value = normalizeTypeData(dataBlock?.type_data);
        // 載入版本來源與狀態
        versionSource.value = Number(dataBlock?.source ?? 0);
        versionSheetStatus.value = dataBlock?.status ?? 0;
        versionUserId.value = Number(dataBlock?.user_id || dataBlock?.user?.id || route.query.user_id || 0) || null;
        versionWorkflow.value = dataBlock?.workflow
            ? {
                  ...dataBlock.workflow,
                  user_id: versionUserId.value
              }
            : null;
        if (consultationId.value) {
            const workspaceResponse = await getConsultationWorkspace(consultationId.value);
            if (workspaceResponse?.status === 1) {
                const workspace = workspaceResponse.data;
                versionWorkflow.value = {
                    ...workspace,
                    consultation: workspace.post_consultation,
                    conclusion_completed: [1, '1', '完成'].includes(workspace.post_consultation?.status),
                    user_id: versionUserId.value
                };
            }
        }
        console.log('[CW workflow data]', {
            route: route.name,
            balanceId: balanceId.value,
            source: versionSource.value,
            status: versionSheetStatus.value,
            workflow: versionWorkflow.value
        });

        // 禁止：進入學員版（含檢視）時自動建立盤點版。
        // 盤點版只應在場次頁「開始諮詢」明確確認後建立，避免誤點產生過多複本。

        // 初始化版本資訊表單
        versionInfoForm.value = {
            internal_name: dataBlock?.internal_name || '',
            external_name: dataBlock?.external_name || '',
            requires_booking: Boolean(
                dataBlock?.requires_booking ?? dataBlock?.booking_required
            )
        };

        // 儲存原始版本資訊快照
        saveOriginalVersionInfo();

        rebuildGrouped();
        try {
            // 預載主列表涉及 type 的選項名稱（使用批次 API 優化）
            const typeIds = new Set();
            (typeData.value || []).forEach((d) => {
                if (d?.type_id != null) typeIds.add(Number(d.type_id));
            });
            const typeIdsArray = Array.from(typeIds);
            if (typeIdsArray.length > 0) {
                // 使用批次 API 一次載入所有類型的選項
                await ensureOptionsBatch(typeIdsArray);
            }
        } catch (e) {
            console.warn('批次載入選項失敗:', e);
        }
        try {
            await preloadAllFieldOptions();
        } catch (e) {
            console.warn('預載欄位選項失敗:', e);
        }
    } catch (e) {
        console.error('loadVersionForEdit error', e);
    }
};

const alignConsultationEditRoute = async () => {
    if (route.name !== 'ConsultationWorkspaceVersionEdit' || !consultationId.value) {
        return;
    }
    const response = await getConsultationWorkspace(consultationId.value);
    if (response?.status !== 1) return;
    const targetId = resolveConsultationWorkspaceVersionId(response.data, balanceId.value);
    if (!targetId || targetId === balanceId.value) return;
    await router.replace({
        name: 'ConsultationWorkspaceVersionEdit',
        params: { id: targetId },
        query: { ...route.query }
    });
};

// 原始資料快照，用於比較差異
const originalUdvData = ref(null);

// 儲存原始資料快照
const saveOriginalUdvData = () => {
    originalUdvData.value = {
        age: Number(udvForm.value.age || 0),
        job: (udvForm.value.job || '').trim(),
        marital_status: Number(udvForm.value.marital_status || 0),
        children: Number(udvForm.value.children || 0)
    };
};

// 檢查資料是否有差異
const hasUdvDataChanged = () => {
    if (!originalUdvData.value) return true; // 如果沒有原始資料，視為有變更

    const current = {
        age: Number(udvForm.value.age || 0),
        job: (udvForm.value.job || '').trim(),
        marital_status: Number(udvForm.value.marital_status || 0),
        children: Number(udvForm.value.children || 0)
    };

    return JSON.stringify(current) !== JSON.stringify(originalUdvData.value);
};

const saveUdv = async (options = {}) => {
    const { silent = false } = options;
    if (!udvId.value) {
        if (!silent) message.warning('找不到個人資訊 ID');
        return { ok: false, skipped: true };
    }
    if (!hasUdvDataChanged()) {
        return { ok: true, skipped: true };
    }
    try {
        udvLoading.value = true;
        const payload = {
            age: Number(udvForm.value.age || 0),
            job: (udvForm.value.job || '').trim(),
            marital_status: Number(udvForm.value.marital_status || 0),
            children: Number(udvForm.value.children || 0)
        };
        const res = await updateUserDetailVersion(Number(udvId.value), payload);
        if (res?.status === 1) {
            if (!silent) message.success('已更新個人資訊');
            saveOriginalUdvData();
            return { ok: true, skipped: false };
        }
        if (!silent) message.error(res?.message || '更新失敗');
        return { ok: false, skipped: false };
    } catch (e) {
        console.error('update user detail error', e);
        if (!silent) message.error('更新失敗');
        return { ok: false, skipped: false };
    } finally {
        udvLoading.value = false;
    }
};

// 類型/列表狀態
const types = ref([]);
const typeIdToClass = ref({}); // { [typeId]: 0|1 }
const selectedAssetTypeId = ref(null);
const selectedLiabilityTypeId = ref(null);
const assetActiveKey = ref('');
const liabilityActiveKey = ref('');
const typeData = ref([]); // 從版本帶回的所有資料
const assetRows = ref([]);
const liabilityRows = ref([]);

// 類型快取
const typeCache = ref({ data: [], fetchedAt: 0 });
const TYPE_CACHE_TTL = 5 * 60 * 1000; // 5 分鐘

const loadTypes = async () => {
    try {
        const now = Date.now();
        
        // 檢查快取
        const cached = typeCache.value;
        const freshEnough = cached.fetchedAt && (now - cached.fetchedAt < TYPE_CACHE_TTL);
        
        if (freshEnough) {
            types.value = cached.data;
            const map = {};
            types.value.forEach((t) => {
                if (t && t.id != null) map[t.id] = t.__classNum;
            });
            typeIdToClass.value = map;
            const assetType = types.value.find((t) => map[t.id] === 0);
            const liabilityType = types.value.find((t) => map[t.id] === 1);
            selectedAssetTypeId.value = assetType?.id ?? null;
            selectedLiabilityTypeId.value = liabilityType?.id ?? null;
            return;
        }
        
        // 單次請求（後端回傳全列表，含 class_name 與 sort）
        // 類型數量通常不會超過 50 筆，限制為 50 筆已足夠
        const res = await getTypes({ page_size: 50 });
        const list = res?.data || res || [];
        const normalized = Array.isArray(list)
            ? list
                  .map((t) => ({
                      ...t,
                      __classNum:
                          t.class_name === '資產'
                              ? 0
                              : t.class_name === '負債'
                              ? 1
                              : null
                  }))
                  .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0))
            : [];
        types.value = normalized;
        
        // 更新快取
        typeCache.value = {
            data: normalized,
            fetchedAt: now
        };
        
        const map = {};
        types.value.forEach((t) => {
            if (t && t.id != null) map[t.id] = t.__classNum;
        });
        typeIdToClass.value = map;
        // 預設選取第一個資產/負債類型
        const assetType = types.value.find((t) => map[t.id] === 0);
        const liabilityType = types.value.find((t) => map[t.id] === 1);
        selectedAssetTypeId.value = assetType?.id ?? null;
        selectedLiabilityTypeId.value = liabilityType?.id ?? null;
    } catch (e) {
        console.error('loadTypes error', e);
        message.error('無法載入資產／負債類型，請重新整理後再試');
        types.value = [];
        typeIdToClass.value = {};
        selectedAssetTypeId.value = null;
        selectedLiabilityTypeId.value = null;
    }
};

// 供 Tabs 使用的 items（Ant Design Vue v4 推薦）
const assetTabItems = computed(() =>
    types.value
        .filter((t) => typeIdToClass.value[t.id] === 0)
        .map((t) => ({ key: String(t.id), label: t.name || `類型#${t.id}` }))
);
const liabilityTabItems = computed(() =>
    types.value
        .filter((t) => typeIdToClass.value[t.id] === 1)
        .map((t) => ({ key: String(t.id), label: t.name || `類型#${t.id}` }))
);

// 同步 tabs activeKey 與內部 selectedTypeId（雙向）
watch(
    selectedAssetTypeId,
    (id) => {
        assetActiveKey.value = id != null ? String(id) : '';
    },
    { immediate: true }
);
watch(
    selectedLiabilityTypeId,
    (id) => {
        liabilityActiveKey.value = id != null ? String(id) : '';
    },
    { immediate: true }
);
watch(
    assetActiveKey,
    (k) => {
        selectedAssetTypeId.value = k ? Number(k) : null;
    },
    { immediate: true }
);
watch(
    liabilityActiveKey,
    (k) => {
        selectedLiabilityTypeId.value = k ? Number(k) : null;
    },
    { immediate: true }
);

const rebuildGrouped = () => {
    const map = typeIdToClass.value;
    const assets = [];
    const liabilities = [];
    typeData.value.forEach((d) => {
        const cls = map[d.type_id];
        if (cls === 1) liabilities.push(d);
        else assets.push(d);
    });
    assetRows.value = assets;
    liabilityRows.value = liabilities;
};

// 選項名稱快取：依 type_id → { option_id: option_name }
const optionNameCache = ref({}); // { [typeId]: { [optionId]: name } }
const optionFetchedAt = ref({}); // { [typeId]: ts }
const optionPending = ref({}); // { [typeId]: boolean }

// 批次載入多個類型的選項（優化版）
const ensureOptionsBatch = async (typeIds, classType = null) => {
    if (!typeIds || typeIds.length === 0) return;
    
    const now = Date.now();
    const needFetch = [];
    const typeIdToClassMap = {};
    
    // 過濾出需要載入的類型（檢查快取和 TTL）
    typeIds.forEach((tid) => {
        const id = Number(tid);
        if (!id) return;
        
        const fresh =
            optionFetchedAt.value[id] &&
            now - optionFetchedAt.value[id] < DETAIL_TTL_MS;
        
        if (!fresh && !optionPending.value[id]) {
            needFetch.push(id);
            // 推斷 class
            let cls = classType;
            if (cls == null) {
                cls = typeIdToClass.value[id];
                if (cls == null) {
                    const t = types.value.find((x) => Number(x.id) === id);
                    cls = t?.class ?? t?.type_class ?? null;
                }
            }
            typeIdToClassMap[id] = cls;
        }
    });
    
    if (needFetch.length === 0) return;
    
    // 標記為載入中
    needFetch.forEach((id) => {
        optionPending.value[id] = true;
    });
    
    try {
        // 後端 batch API 可同時處理資產與負債 type，只送一支請求。
        const res = await getOptionsBatch({ type_ids: needFetch });
        if (res?.status !== 1 || !res?.data) throw new Error('選項批次載入失敗');
        Object.entries(res.data).forEach(([typeIdStr, options]) => {
            const typeId = Number(typeIdStr);
            const map = {};
            (Array.isArray(options) ? options : []).forEach((option) => {
                if (option?.id != null) map[Number(option.id)] = option.name ?? String(option.id);
            });
            optionNameCache.value[typeId] = map;
            optionListCache.value[typeId] = (Array.isArray(options) ? options : []).map((option) => ({
                value: option.id,
                label: option.name,
                hint: option.hint,
                value_required: option.value_required,
                cash_flow_required: option.cash_flow_required
            }));
            optionListFetchedAt.value[typeId] = now;
            optionFetchedAt.value[typeId] = now;
        });
    } catch (e) {
        console.error('批次載入選項錯誤（不回退為迴圈請求）:', e);
    } finally {
        needFetch.forEach((id) => {
            optionPending.value[id] = false;
        });
    }
};

// 單一類型選項載入（作為 fallback）
const ensureOptionsForTypeSingle = async (typeId) => {
    const tid = Number(typeId);
    if (!tid) return;
    const now = Date.now();
    const fresh =
        optionFetchedAt.value[tid] &&
        now - optionFetchedAt.value[tid] < DETAIL_TTL_MS;
    if (fresh) return;
    if (optionPending.value[tid]) return;
    optionPending.value[tid] = true;
    try {
        // 推斷 class（0/1）
        let cls = typeIdToClass.value[tid];
        if (cls == null) {
            const t = types.value.find((x) => Number(x.id) === tid);
            cls = t?.class ?? t?.type_class ?? null;
        }
        const res = await getOptions({
            type_id: tid,
            class: cls,
            page_size: 50
        });
        const list = res?.data || res || [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach((o) => {
            if (o && o.id != null) map[Number(o.id)] = o.name ?? String(o.id);
        });
        optionNameCache.value[tid] = map;
        optionFetchedAt.value[tid] = now;
    } catch (e) {
        // 靜默失敗，避免阻塞
    } finally {
        optionPending.value[tid] = false;
    }
};

// 保留舊的函數名稱作為向後相容
const ensureOptionsForType = ensureOptionsForTypeSingle;

const getOptionName = (typeId, optionId, fallback) => {
    const map = optionNameCache.value[Number(typeId)] || {};
    return map[Number(optionId)] ?? fallback ?? '';
};

watch(typeIdToClass, () => {
    rebuildGrouped();
});

// 從目前 rows 蒐集所有 detail_id，預先載入對應 options 以供顯示中文標籤
const collectDetailIdsFromRows = (rows) => {
    const ids = new Set();
    (rows || []).forEach((r) => {
        (r.fields || []).forEach((f) => {
            const id = Number(f.field_id);
            if (!Number.isNaN(id) && id) ids.add(id);
        });
    });
    return Array.from(ids);
};

// 批次載入多個欄位的詳細狀態（優化版）
const ensureDetailOptionsBatch = async (fieldIds) => {
    if (!fieldIds || fieldIds.length === 0) return;
    
    const now = Date.now();
    const needFetch = [];
    
    // 過濾出需要載入的欄位（檢查快取和 TTL）
    fieldIds.forEach((fid) => {
        const id = Number(fid);
        if (!id) return;
        
        const hasCached =
            Array.isArray(detailOptionsMap.value[id]) &&
            detailOptionsMap.value[id].length > 0;
        const freshEnough =
            hasCached &&
            detailFetchedAt.value[id] &&
            now - detailFetchedAt.value[id] < DETAIL_TTL_MS;
        
        if (!freshEnough && !detailPendingMap.value[id]) {
            needFetch.push(id);
        }
    });
    
    if (needFetch.length === 0) return;
    
    // 標記為載入中
    needFetch.forEach((id) => {
        detailPendingMap.value[id] = true;
        detailLoadingMap.value[id] = true;
    });
    
    try {
        const res = await getDetailStatusBatch({
            field_ids: needFetch
        });
        
        if (res?.status === 1 && res?.data) {
            // 處理批次回應：{ [field_id]: [statuses...] }
            Object.entries(res.data).forEach(([fieldIdStr, statuses]) => {
                const fieldId = Number(fieldIdStr);
                const mapped = Array.isArray(statuses)
                    ? statuses.map((d) => {
                          const name = d.name ?? d.label ?? String(d.id || '');
                          const value = d.id ?? d.value ?? String(d.id || '');
                          return { value: String(value), label: name };
                      })
                    : [];
                
                detailOptionsMap.value[fieldId] = mapped;
                detailFetchedAt.value[fieldId] = now;
            });
        }
    } catch (e) {
        console.warn('批次載入詳細狀態失敗（不回退為迴圈請求）:', e);
    } finally {
        needFetch.forEach((id) => {
            detailPendingMap.value[id] = false;
            detailLoadingMap.value[id] = false;
        });
    }
};

const preloadAllFieldOptions = async () => {
    try {
        const assetIds = collectDetailIdsFromRows(assetRows.value);
        const liabilityIds = collectDetailIdsFromRows(liabilityRows.value);
        const all = Array.from(new Set([...assetIds, ...liabilityIds]));
        
        if (all.length === 0) return;
        
        // 使用批次 API 一次載入所有欄位的詳細狀態
        await ensureDetailOptionsBatch(all);
    } catch (e) {
        console.warn('預載欄位選項失敗:', e);
    }
};

// 缺漏提示資料
const missingAsset = ref({ list: [], hint: '' });
const missingLiability = ref({ list: [], hint: '' });
const loadingMissingAsset = ref(false);
const loadingMissingLiability = ref(false);
// 缺漏提示快取
const missingCache = ref({}); // { [classType_typeId]: { list, hint, timestamp } }

const loadMissing = async (classType) => {
    if (!isEdit.value) return;
    if (isReadonly.value) return; // 查看模式不呼叫缺漏 API
    // 根據當前選擇的 typeId 去查缺漏
    if (classType === 0) {
        const typeId = Number(selectedAssetTypeId.value);
        if (!typeId) {
            missingAsset.value = { list: [], hint: '' };
            return;
        }

        // 檢查快取
        const cacheKey = `0_${typeId}`;
        const now = Date.now();
        const cached = missingCache.value[cacheKey];
        const freshEnough =
            cached &&
            cached.timestamp &&
            now - cached.timestamp < DETAIL_TTL_MS;

        if (freshEnough) {
            missingAsset.value = { list: cached.list, hint: cached.hint || '' };
            return;
        }

        loadingMissingAsset.value = true;
        try {
            const res = await getMissingEntries(balanceId.value, 0, typeId);
            const data = res?.data || {};
            const list = Array.isArray(data.list)
                ? data.list.filter((x) => x)
                : [];
            // 過濾出與當前選擇的 typeId 相符的缺漏項目
            // type_id 是當前選擇的類型，missing_type_id 是建議新增的類型
            const filtered = list.filter((x) => Number(x.type_id) === typeId);
            const result = {
                list: filtered,
                hint: data.hint || ''
            };
            missingAsset.value = result;
            // 更新快取
            missingCache.value[cacheKey] = {
                ...result,
                timestamp: now
            };
        } catch (e) {
            missingAsset.value = { list: [], hint: '' };
        } finally {
            loadingMissingAsset.value = false;
        }
    } else {
        const typeId = Number(selectedLiabilityTypeId.value);
        if (!typeId) {
            missingLiability.value = { list: [], hint: '' };
            return;
        }

        // 檢查快取
        const cacheKey = `1_${typeId}`;
        const now = Date.now();
        const cached = missingCache.value[cacheKey];
        const freshEnough =
            cached &&
            cached.timestamp &&
            now - cached.timestamp < DETAIL_TTL_MS;

        if (freshEnough) {
            missingLiability.value = {
                list: cached.list,
                hint: cached.hint || ''
            };
            return;
        }

        loadingMissingLiability.value = true;
        try {
            const res = await getMissingEntries(balanceId.value, 1, typeId);
            const data = res?.data || {};
            const list = Array.isArray(data.list)
                ? data.list.filter((x) => x)
                : [];
            // 過濾出與當前選擇的 typeId 相符的缺漏項目
            // type_id 是當前選擇的類型，missing_type_id 是建議新增的類型
            const filtered = list.filter((x) => Number(x.type_id) === typeId);
            const result = {
                list: filtered,
                hint: data.hint || ''
            };
            missingLiability.value = result;
            // 更新快取
            missingCache.value[cacheKey] = {
                ...result,
                timestamp: now
            };
        } catch (e) {
            missingLiability.value = { list: [], hint: '' };
        } finally {
            loadingMissingLiability.value = false;
        }
    }
};

// 切換 tabs 時同步刷新缺漏（唯讀時不觸發）
watch(selectedAssetTypeId, (v) => {
    if (isEdit.value && !isReadonly.value && currentStep.value === 2)
        loadMissing(0);
});
watch(selectedLiabilityTypeId, (v) => {
    if (isEdit.value && !isReadonly.value && currentStep.value === 3)
        loadMissing(1);
});

// 新增/編輯彈窗狀態（共用）
const modalVisible = ref(false);
const modalLoading = ref(false);
const modalMode = ref('add'); // 'add' | 'edit'
const modalClassType = ref(0); // 0 資產 1 負債
const modalTypeId = ref(null);
const modalRecord = ref(null);
const modalForm = ref({
    option_id: '',
    name: '',
    amount: 0,
    monthly_interest_expense: 0
});
const modalFields = ref([]); // 從 getFields 取得的定義
const modalFieldValues = ref({});
const flatExtraFieldsExpanded = ref(false);
const optionList = ref([]);
const optionLoading = ref(false);
const allFields = ref([]); // 儲存所有欄位定義，用於總覽頁面顯示

// 依目前選取的 option 取得其 meta（hint、必填設定）
const selectedOptionMeta = computed(() => {
    const currentId = Number(modalForm.value?.option_id);
    return (
        (optionList.value || []).find((o) => Number(o.value) === currentId) ||
        null
    );
});

// 判斷 API 回來的 0/1、true/false、'1'/'0' 等 truthy 旗標
const isTruthyFlag = (v) =>
    v === 1 ||
    v === true ||
    String(v).toLowerCase() === '1' ||
    String(v).toLowerCase() === 'true';

// 動態必填：金額、月收入/月支出
const amountIsRequired = computed(() =>
    isTruthyFlag(selectedOptionMeta.value?.value_required)
);
const cashFlowIsRequired = computed(() =>
    isTruthyFlag(selectedOptionMeta.value?.cash_flow_required)
);

// 簡易判斷空值（允許數字 0）
const isEmptyValue = (v) =>
    v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

// 表單檢核（未通過時回傳錯誤訊息陣列）
const validateModalForm = () => {
    const errors = [];
    // 選項必填
    if (!modalForm.value.option_id && Number(modalForm.value.option_id) !== 0) {
        errors.push('請選擇選項');
    }
    // 名稱永遠必填
    if (isEmptyValue(modalForm.value.name)) {
        errors.push('請輸入名稱');
    }
    // 金額依選項 value_required
    if (amountIsRequired.value) {
        const amt = modalForm.value.amount;
        if (amt === undefined || amt === null || amt === '') {
            errors.push('請填寫金額');
        }
    }
    // 月收入/月支出 依選項 cash_flow_required
    if (cashFlowIsRequired.value) {
        const cf = modalForm.value.monthly_interest_expense;
        if (cf === undefined || cf === null || cf === '') {
            errors.push(
                modalClassType.value === 0 ? '請填寫月收入' : '請填寫月支出'
            );
        }
    }
    // 額外字段：只要有顯示就強制必填
    (modalFields.value || []).forEach((f) => {
        if (!shouldShowField(f)) return;
        const val = modalFieldValues.value[f.id];
        if (isEmptyValue(val)) {
            errors.push(`請填寫字段：${f.name || '#' + f.id}`);
        }
    });
    return errors;
};

const initAddModalState = (classType, typeId) => {
    modalMode.value = 'add';
    modalClassType.value = classType;
    const tid =
        typeId != null
            ? Number(typeId)
            : classType === 0
            ? selectedAssetTypeId.value
            : selectedLiabilityTypeId.value;
    modalTypeId.value = tid;
    if (modalTypeId.value == null) {
        const candidates = types.value.filter(
            (t) => (t.class ?? t.type_class) === classType
        );
        modalTypeId.value = candidates.length > 0 ? candidates[0].id : null;
    }
    modalRecord.value = null;
    modalForm.value = {
        option_id: '',
        name: '',
        amount: 0,
        monthly_interest_expense: 0
    };
    modalFieldValues.value = {};
};

const openAddModal = async (classType, typeId = null) => {
    initAddModalState(classType, typeId);
    // 先開啟視窗，再載入資料（非阻塞）
    modalVisible.value = true;
    // 背景載入選項列表
    loadOptionsForType(modalTypeId.value).catch((e) => {
        console.error('載入選項列表失敗:', e);
    });
};

// 依指定類型直接開啟新增（用於缺漏提示快速導引）
const openAddForType = async (classType, typeId, optionId) => {
    // 使用傳入的 typeId 和 classType，而不是當前頁面選擇的
    initAddModalState(classType, typeId);
    // 先開啟視窗
    modalVisible.value = true;

    if (optionId != null) {
        const optionIdNum = Number(optionId);
        // 背景載入選項列表，載入完成後設定選項
        loadOptionsForType(modalTypeId.value)
            .then(async () => {
                await nextTick();
                const selectedOption = optionList.value.find(
                    (opt) => Number(opt.value) === optionIdNum
                );
                if (selectedOption) {
                    modalForm.value.option_id = optionIdNum;
                    // 自動將名稱設定為選項名稱
                    if (selectedOption.label) {
                        modalForm.value.name = selectedOption.label;
                    }
                    // 背景載入欄位
                    loadFieldsForOption(optionIdNum).catch((e) => {
                        console.error('載入欄位失敗:', e);
                    });
                } else {
                    console.warn(
                        `選項 ID ${optionIdNum} 不存在於類型 ${typeId} 的選項列表中`
                    );
                }
            })
            .catch((e) => {
                console.error('載入選項列表失敗:', e);
            });
    } else {
        // 背景載入選項列表
        loadOptionsForType(modalTypeId.value).catch((e) => {
            console.error('載入選項列表失敗:', e);
        });
    }
};

const openEditModal = async (classType, record) => {
    modalMode.value = 'edit';
    modalClassType.value = classType;
    modalTypeId.value = record.type_id;
    modalRecord.value = record;
    modalForm.value = {
        option_id: record.option_id,
        name: record.name,
        amount: Number(record.amount || 0),
        monthly_interest_expense: Number(record.monthly_interest_expense || 0)
    };

    // 先將現有 fields 展平成值物件（同步操作，不需要等待）
    modalFieldValues.value = {};
    (record.fields || []).forEach((f) => collectFieldValues(f));

    // 先開啟視窗，再載入資料（非阻塞）
    modalVisible.value = true;

    // 並行載入選項列表和欄位定義
    Promise.all([
        loadOptionsForType(modalTypeId.value),
        loadFieldsForOption(record.option_id)
    ]).catch((e) => {
        console.error('載入資料失敗:', e);
    });
};

const openFlatAdd = async ({ classType, type, optionId, anchor }) => {
    flatExtraFieldsExpanded.value = false;
    flatEditorAnchor.value = anchor;
    await openAddForType(classType, type.id, optionId);
};

const openFlatEdit = async ({ classType, row, anchor }) => {
    flatExtraFieldsExpanded.value = false;
    flatEditorAnchor.value = anchor;
    await openEditModal(classType, row);
};

const openFlatSuggestion = async (payload) => {
    await openFlatAdd(payload);
};

const collectFieldValues = (fieldNode) => {
    if (!fieldNode) return;
    // 總結頁面的欄位資料使用 field_id，對應欄位定義的 id
    const fieldId = fieldNode.field_id ?? fieldNode.id;
    if (fieldId != null) {
        let value = fieldNode.value ?? '';
        // 檢查是否為布林欄位，需要從 modalFields 查找欄位定義
        const fieldDefinition = modalFields.value.find(
            (f) => f.id === fieldId || f.field_id === fieldId
        );
        const isBooleanField =
            fieldNode.type === 'boolean' ||
            fieldNode.input_type === 'boolean' ||
            fieldDefinition?.type === 'boolean' ||
            fieldDefinition?.input_type === 'boolean';
        if (isBooleanField) {
            // 將字串 '0'/'1' 轉換為數字 0/1
            if (value === '0' || value === 0) value = 0;
            else if (value === '1' || value === 1) value = 1;
            else value = 0; // 預設為 0
        }
        modalFieldValues.value[fieldId] = value;
    }
    // 處理 fields 陣列
    if (Array.isArray(fieldNode.fields)) {
        fieldNode.fields.forEach((child) => collectFieldValues(child));
    }
    // 處理 children 陣列（巢狀欄位）
    if (Array.isArray(fieldNode.children)) {
        fieldNode.children.forEach((child) => collectFieldValues(child));
    }
};

// 選項列表快取（用於下拉選單，包含 meta）
const optionListCache = ref({}); // { [typeId]: [{value, label, hint, ...}] }
const optionListFetchedAt = ref({}); // { [typeId]: timestamp }

const loadOptionsForType = async (typeId) => {
    const resolvedTypeId = Number(typeId);
    if (!resolvedTypeId) {
        optionList.value = [];
        return;
    }

    // 檢查快取
    const now = Date.now();
    const cached = optionListCache.value[resolvedTypeId];
    const freshEnough =
        cached &&
        optionListFetchedAt.value[resolvedTypeId] &&
        now - optionListFetchedAt.value[resolvedTypeId] < DETAIL_TTL_MS;

    if (freshEnough) {
        optionList.value = cached;
        return;
    }

    optionLoading.value = true;
    try {
        // 優先使用 modalClassType（明確指定的類別），只有在未設置時才根據 typeId 推斷
        let cls = modalClassType.value;
        if (cls == null) {
            cls = typeIdToClass.value[resolvedTypeId];
            if (cls == null) {
                const t = types.value.find(
                    (x) => Number(x.id) === resolvedTypeId
                );
                cls = t?.class ?? t?.type_class ?? null;
            }
        }
        const res = await getOptions({
            type_id: resolvedTypeId,
            class: cls,
            page_size: 100
        });
        const list = res?.data || res || [];
        // 保留必要 meta，供必填與 hint 顯示
        const mapped = Array.isArray(list)
            ? list.map((o) => ({
                  value: o.id,
                  label: o.name,
                  hint: o.hint,
                  value_required: o.value_required,
                  cash_flow_required: o.cash_flow_required
              }))
            : [];
        optionList.value = mapped;
        // 更新快取
        optionListCache.value[resolvedTypeId] = mapped;
        optionListFetchedAt.value[resolvedTypeId] = now;
    } catch (e) {
        console.error('loadOptions error', e);
        optionList.value = [];
    } finally {
        optionLoading.value = false;
    }
};

const relations = ref([]);
const flatRelations = ref([]);
const flatEditorAnchor = ref('');
const flatFieldsByOption = ref({});
const flatSaveLoading = ref(false);
const flatSheetRef = ref(null);

const preloadFlatFieldDefinitions = async () => {
    if (Object.keys(flatFieldsByOption.value).length) return;
    try {
        // fields API 本身已回傳 option_id；一次取回後分組，
        // 避免過去每個 option 各打一支請求造成 429。
        const result = await getFields({ page: 1, page_size: 100, mode: 'tree' });
        const list = result?.data || result || [];
        const fields = Array.isArray(list) ? list : [];
        allFields.value = fields;
        flatFieldsByOption.value = fields.reduce(
            (groups, field) => {
                const optionId = Number(field.option_id);
                if (!optionId) return groups;
                if (!groups[optionId]) groups[optionId] = [];
                groups[optionId].push(field);
                return groups;
            },
            {}
        );
    } catch {
        flatFieldsByOption.value = {};
    }
};

const workspaceTypes = (batches = []) => batches.map(({ typeId, data }) => ({
    type_id: Number(typeId),
    items: data.map((item, index) => ({ ...item, client_key: `type-${typeId}-${index}` }))
}));

const saveFlatSheet = async ({ batches }, options = {}) => {
    if (flatSaveLoading.value) return false;
    flatSaveLoading.value = true;
    try {
        const response = await saveVersionWorkspace(balanceId.value, {
            finish: false,
            types: workspaceTypes(batches)
        });
        assertSuccessfulSheetResponse(response, '儲存資料失敗');
        await loadVersionForEdit();
        await loadSummary();
        if (!options.silent) {
            const label = Number(versionSource.value) === 2 ? '諮詢版' : '盤點版';
            message.success(isConsultantWorkspaceRoute.value ? `${label}已儲存` : '草稿已儲存');
        }
        return true;
    } catch (error) {
        console.error('saveFlatSheet error', error);
        message.error('儲存失敗，請確認資料後重試');
        return false;
    } finally {
        flatSaveLoading.value = false;
    }
};

const saveFlatSheetFromSummary = async () => {
    const prepared = flatSheetRef.value?.prepareSave();
    if (!prepared?.ok) {
        message.error(prepared?.message || '請先完成表格中標示的必填資料');
        return;
    }
    await saveFlatSheet(prepared.payload);
};

const loadRelationsForOption = async (optionId) => {
    try {
        const res = await getOptionRelations({
            option_id: Number(optionId),
            page_size: 50
        });
        const list = res?.data || res || [];
        relations.value = Array.isArray(list) ? list : [];
    } catch (e) {
        console.error('loadRelations error', e);
        relations.value = [];
    }
};

// 依據 option_relations 的規則來決定欄位顯示
const toNumberSafe = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? v : n;
};

const normalizeOperator = (op) => {
    const o = String(op || 'eq').toLowerCase();
    if (o === '=' || o === '==') return 'eq';
    if (o === '!=' || o === '<>') return 'neq';
    if (o === '>') return 'gt';
    if (o === '>=') return 'gte';
    if (o === '<') return 'lt';
    if (o === '<=') return 'lte';
    if (o === 'in' || o === 'includes') return 'in';
    if (o === 'not_in' || o === 'nin') return 'nin';
    return 'eq';
};

const evaluateCondition = (left, op, rightRaw) => {
    const operator = normalizeOperator(op);
    const leftVal = toNumberSafe(left);
    const rightVal = Array.isArray(rightRaw)
        ? rightRaw
        : typeof rightRaw === 'string'
        ? rightRaw
              .split(',')
              .map((x) => x.trim())
              .filter((x) => x !== '')
        : [rightRaw];
    const rightVals = rightVal.map((v) => toNumberSafe(v));
    if (operator === 'eq')
        return String(leftVal) === String(rightVals[0] ?? '');
    if (operator === 'neq')
        return String(leftVal) !== String(rightVals[0] ?? '');
    if (operator === 'gt') return Number(leftVal) > Number(rightVals[0] ?? 0);
    if (operator === 'gte') return Number(leftVal) >= Number(rightVals[0] ?? 0);
    if (operator === 'lt') return Number(leftVal) < Number(rightVals[0] ?? 0);
    if (operator === 'lte') return Number(leftVal) <= Number(rightVals[0] ?? 0);
    if (operator === 'in')
        return rightVals.some((v) => String(v) === String(leftVal));
    if (operator === 'nin')
        return !rightVals.some((v) => String(v) === String(leftVal));
    return true;
};

const shouldShowField = (fieldDef, checkedIds = new Set()) => {
    if (!fieldDef) return true;

    const fieldId = Number(fieldDef.id);

    // 防止循環依賴
    if (checkedIds.has(fieldId)) return true;
    checkedIds.add(fieldId);

    // 優先使用 field 本身的 dependency 資訊
    const dependencyFieldId = fieldDef.dependency_field_id;
    const dependencyValue = fieldDef.dependency_value;

    if (dependencyFieldId != null && dependencyValue != null) {
        // 找到依賴的字段定義
        const dependsField = modalFields.value.find(
            (f) => Number(f.id) === Number(dependencyFieldId)
        );

        // 遞迴檢查依賴字段是否應該顯示
        if (dependsField && !shouldShowField(dependsField, checkedIds)) {
            // 依賴字段本身不可見，則此字段也不可見
            return false;
        }

        // 檢查依賴字段的值是否符合條件
        const currentValue =
            modalFieldValues.value[Number(dependencyFieldId)] ?? '';
        const pass = evaluateCondition(currentValue, 'eq', dependencyValue);

        if (!pass) return false;
    }

    // 回退到使用 relations（舊版 API）
    if (Array.isArray(relations.value) && relations.value.length > 0) {
        const rules = relations.value.filter((r) => {
            const targetId =
                r.field_id ??
                r.target_field_id ??
                r.to_field_id ??
                r.child_field_id;
            return Number(targetId) === fieldId;
        });

        if (rules.length > 0) {
            return rules.every((r) => {
                const dependsId =
                    r.condition_field_id ??
                    r.parent_field_id ??
                    r.when_field_id ??
                    r.depends_on ??
                    r.source_field_id;

                const dependsField = modalFields.value.find(
                    (f) => Number(f.id) === Number(dependsId)
                );

                if (
                    dependsField &&
                    !shouldShowField(dependsField, checkedIds)
                ) {
                    return false;
                }

                const op = r.operator ?? r.op ?? 'eq';
                const expected = r.value ?? r.expected_value ?? r.values ?? '';
                const visibleFlag = r.show ?? r.visible ?? r.is_visible;
                const current = modalFieldValues.value[Number(dependsId)] ?? '';
                const pass = evaluateCondition(current, op, expected);

                if (
                    visibleFlag == null ||
                    String(visibleFlag) === '1' ||
                    visibleFlag === true
                )
                    return pass;
                return !pass;
            });
        }
    }

    return true;
};

// 陣列字段子彈窗
const arrayModalVisible = ref(false);
const arrayModalField = ref(null);
const arrayChoices = ref([]);
const arraySelected = ref([]);
const arrayLoading = ref(false);
// 查看單筆資料彈窗（只讀）
const viewItemVisible = ref(false);
const viewItemRecord = ref(null);
const openViewItemModal = (record) => {
    viewItemRecord.value = record;
    viewItemVisible.value = true;
};

// 打開新增/編輯時，預載可能會用到的欄位 options
// 避免在彈窗開啟時引發大量請求，改為在版本資料載入後集中預載

// 欄位詳細選項快取（array 用）
const detailOptionsMap = ref({}); // { [fieldId]: [{value,label}] }
const detailLoadingMap = ref({}); // { [fieldId]: boolean }
const detailFetchedAt = ref({}); // { [fieldId]: timestamp }
const detailPendingMap = ref({}); // { [fieldId]: boolean }
const DETAIL_TTL_MS = 5 * 60 * 1000; // 5 分鐘 TTL，避免重複打 API

// 單一欄位詳細狀態載入（作為 fallback）
const ensureDetailOptionsSingle = async (fieldId) => {
    const id = Number(fieldId);
    if (!id) return;
    const now = Date.now();
    const hasCached =
        Array.isArray(detailOptionsMap.value[id]) &&
        detailOptionsMap.value[id].length > 0;
    const freshEnough =
        hasCached &&
        detailFetchedAt.value[id] &&
        now - detailFetchedAt.value[id] < DETAIL_TTL_MS;
    if (freshEnough) return;
    if (detailPendingMap.value[id]) return; // 避免重複請求
    detailPendingMap.value[id] = true;
    detailLoadingMap.value[id] = true;
    try {
        const res = await getDetailStatus({
            field_id: id,
            page: 1,
            page_size: 50
        }).catch((e) => {
            // 攔截無權限錯誤，友善提示一次，不反覆洗版
            const msg =
                e && (e.message || e.msg) ? String(e.message || e.msg) : '';
            const text = (res) => res?.message || res?.msg || '';
            // 交由下方 catch 處理，這裡只為了擋掉未被 try/catch 捕捉的 rejected
            throw e;
        });
        const list = res?.data || res || [];
        const mapped = Array.isArray(list)
            ? list.map((d) => {
                  // 將 detail-status 的 id 當作 value，name 當作 label
                  const name = d.name ?? d.label ?? String(d.id || '');
                  const value = d.id ?? d.value ?? String(d.id || '');
                  return { value: String(value), label: name };
              })
            : [];
        // 主要以 detail_id（API參數 id）作為 key 快取
        detailOptionsMap.value[id] = mapped;
        // 若回傳項目含 field_id，額外同步到以 field_id 為 key 的快取，避免鍵不一致
        const serverFieldId = Number(
            Array.isArray(list) && list.length > 0
                ? list[0].field_id ?? list[0].fid ?? null
                : null
        );
        if (!Number.isNaN(serverFieldId) && serverFieldId) {
            detailOptionsMap.value[serverFieldId] = mapped;
        }
        detailFetchedAt.value[id] = now;
    } catch (e) {
        // 無權限或其他錯誤時，明確提醒；只提示一次避免洗版
        const em = String(e?.message || e?.msg || '');
        const isUnauthorized = /unauthori|forbid|無權限/i.test(em);
        if (isUnauthorized && !detailLoadingMap.value.__warned__) {
            detailLoadingMap.value.__warned__ = true;
            message.warning('無權限取得詳細字段字典，顯示將使用原始值');
        }
        // 保留現有快取不動，避免空值再次觸發重試風暴
    } finally {
        detailLoadingMap.value[id] = false;
        detailPendingMap.value[id] = false;
    }
};

// 保留舊的函數名稱作為向後相容（單一欄位載入）
const ensureDetailOptions = ensureDetailOptionsSingle;

// 單選（array 型但只允許一筆）取值/設值
const getArraySingleValue = (fieldId) => {
    const raw = modalFieldValues.value[fieldId];
    if (Array.isArray(raw)) return raw.length > 0 ? String(raw[0]) : undefined;
    if (typeof raw === 'string') {
        const parts = raw
            .split(',')
            .map((x) => x.trim())
            .filter((x) => x !== '');
        return parts.length > 0 ? String(parts[0]) : undefined;
    }
    if (raw == null) return undefined;
    return String(raw);
};

const setArraySingleValue = (fieldId, val) => {
    if (val == null || val === '') {
        modalFieldValues.value[fieldId] = '';
        return;
    }
    modalFieldValues.value[fieldId] = String(val);
};

// 以 fields 回傳的 type_data 為優先作為下拉選項；沒有再用 detail-status 回退
const getFieldOptions = (field) => {
    if (Array.isArray(field?.type_data) && field.type_data.length > 0) {
        return field.type_data.map((d) => {
            const name = d.name ?? d.label ?? String(d.id || '');
            const value = d.id ?? d.value ?? String(d.id || '');
            return { value: String(value), label: name };
        });
    }
    // 修正：使用 field.id 而不是 field.field_id，因為 ensureDetailOptions 使用 field.id 作為快取 key
    return detailOptionsMap.value[field.id] || [];
};

const getFieldLoading = (field) => {
    if (Array.isArray(field?.type_data) && field.type_data.length > 0)
        return false;
    // 修正：使用 field.id 而不是 field.field_id，保持一致性
    return detailLoadingMap.value[field.id] === true;
};

const handleArrayDropdown = async (open, field) => {
    if (!open) return;
    if (Array.isArray(field?.type_data) && field.type_data.length > 0) return;
    // 修正：使用 field.id 而不是 field.field_id，保持一致性
    await ensureDetailOptions(field.id);
};

const openArrayModal = async (fieldDef) => {
    arrayModalField.value = fieldDef;
    arraySelected.value = [];
    arrayChoices.value = [];
    arrayLoading.value = true;
    try {
        const res = await getDetailStatus({
            field_id: Number(fieldDef.id),
            page_size: 50
        });
        const list = res?.data || res || [];
        // 轉成 {value,label}
        arrayChoices.value = Array.isArray(list)
            ? list.map((d) => ({
                  value: d.id ?? d.value ?? String(d.id || ''),
                  label: d.name ?? d.label ?? String(d.id || '')
              }))
            : [];
    } catch (e) {
        console.error('load detail-status error', e);
    } finally {
        arrayLoading.value = false;
        arrayModalVisible.value = true;
    }
};

const confirmArrayModal = () => {
    if (!arrayModalField.value) return;
    // 先用逗號字串保存
    modalFieldValues.value[arrayModalField.value.id] =
        arraySelected.value.join(',');
    arrayModalVisible.value = false;
};

// 欄位定義快取
const fieldsCache = ref({}); // { [optionId]: [fields] }
const fieldsFetchedAt = ref({}); // { [optionId]: timestamp }
const relationsCache = ref({}); // { [optionId]: [relations] }

// 覆寫 loadFieldsForOption，載入 relations
const loadFieldsForOption = async (optionId) => {
    const optionIdNum = Number(optionId);
    if (!optionIdNum) {
        modalFields.value = [];
        relations.value = [];
        return;
    }

    // 檢查快取
    const now = Date.now();
    const cachedFields = fieldsCache.value[optionIdNum];
    const cachedRelations = relationsCache.value[optionIdNum];
    const freshEnough =
        cachedFields &&
        fieldsFetchedAt.value[optionIdNum] &&
        now - fieldsFetchedAt.value[optionIdNum] < DETAIL_TTL_MS;

    if (freshEnough) {
        modalFields.value = cachedFields;
        relations.value = cachedRelations || [];
        // 初始化 field 值（保留既有）
        modalFields.value.forEach((f) => {
            if (modalFieldValues.value[f.id] == null) {
                // 布林欄位預設為 0（否）
                if (f.type === 'boolean' || f.input_type === 'boolean') {
                    modalFieldValues.value[f.id] = 0;
                } else {
                    modalFieldValues.value[f.id] = '';
                }
            }
        });
        return;
    }

    try {
        // 並行載入 fields 和 relations
        const [fieldsRes, relationsRes] = await Promise.all([
            getFields({
                option_id: optionIdNum,
                page_size: 100
            }),
            getOptionRelations({
                option_id: optionIdNum,
                page_size: 50
            })
        ]);

        const list = fieldsRes?.data || fieldsRes || [];
        const fields = Array.isArray(list) ? list : [];
        modalFields.value = fields;

        const relationsList = relationsRes?.data || relationsRes || [];
        relations.value = Array.isArray(relationsList) ? relationsList : [];

        // 更新快取
        fieldsCache.value[optionIdNum] = fields;
        fieldsFetchedAt.value[optionIdNum] = now;
        relationsCache.value[optionIdNum] = relations.value;

        // 初始化 field 值（保留既有）
        modalFields.value.forEach((f) => {
            if (modalFieldValues.value[f.id] == null) {
                // 布林欄位預設為 0（否）
                if (f.type === 'boolean' || f.input_type === 'boolean') {
                    modalFieldValues.value[f.id] = 0;
                } else {
                    modalFieldValues.value[f.id] = '';
                }
            }
        });
    } catch (e) {
        console.error('loadFields error', e);
        modalFields.value = [];
        relations.value = [];
    }
};

const handleOptionChange = async (val) => {
    modalForm.value.option_id = val;
    // 當選項改變時，自動將名稱設定為選項名稱
    const selectedOption = optionList.value.find(
        (opt) => Number(opt.value) === Number(val)
    );
    if (selectedOption && selectedOption.label) {
        modalForm.value.name = selectedOption.label;
    }
    try {
        await loadFieldsForOption(val);
    } catch (error) {
        console.error('載入欄位失敗:', error);
    }
};

const closeModal = () => {
    modalVisible.value = false;
    flatEditorAnchor.value = '';
};

const composeFieldsPayload = () => {
    // 簡化：將平面 field 值轉為一層結構
    // 總結頁面：fieldId 是 field_id，對應欄位定義的 id
    // 步驟頁面：fieldId 是 field.id
    return Object.entries(modalFieldValues.value).map(([fieldId, value]) => ({
        field_id: Number(fieldId), // 總結頁面直接是 field_id，步驟頁面是 field.id
        value: String(value),
        fields: []
    }));
};

const submitModal = async () => {
    modalLoading.value = true;
    try {
        // 送出前檢核必填
        const errors = validateModalForm();
        if (errors.length > 0) {
            if (
                (modalFields.value || []).some(
                    (field) =>
                        shouldShowField(field) &&
                        isEmptyValue(modalFieldValues.value[field.id])
                )
            ) {
                flatExtraFieldsExpanded.value = true;
            }
            message.error(errors[0]);
            return;
        }
        const typeId = Number(modalTypeId.value);
        const newItem = {
            option_id: Number(modalForm.value.option_id),
            name: modalForm.value.name?.trim() || '',
            amount: Number(modalForm.value.amount || 0),
            monthly_interest_expense: Number(
                modalForm.value.monthly_interest_expense || 0
            ),
            fields: composeFieldsPayload()
        };

        const finalList = buildTypeSavePayload(typeData.value, typeId, {
            mode: modalMode.value,
            recordId: modalRecord.value?.data_id,
            item: newItem
        });

        await updateAssetLiability(balanceId.value, typeId, finalList);
        message.success(modalMode.value === 'edit' ? '更新成功' : '新增成功');
        closeModal();
        await reloadVersionData();
        // 重新撈取缺漏提示（維持當前步驟類型）
        try {
            if (currentStep.value === 1) await loadMissing(0);
            else if (currentStep.value === 2) await loadMissing(1);
        } catch {}
    } catch (e) {
        console.error('submit asset/liability error', e);
        message.error('儲存失敗');
    } finally {
        modalLoading.value = false;
    }
};

const handleDeleteRow = (classType, record) => {
    Modal.confirm({
        title: '確認刪除這筆資料？',
        okText: '刪除',
        cancelText: '取消',
        maskClosable: true,
        onOk: async () => {
            try {
                // 依規格：移除呼叫 /api/admin/asset_liability/{data_id}
                const { deleteAssetLiability } = await import(
                    '@/api/finance/assetLiability'
                );
                await deleteAssetLiability(record.data_id);
                message.success('刪除成功');
                await reloadVersionData();
            } catch (e) {
                console.error('delete row error', e);
                message.error('刪除失敗');
            }
        }
    });
};

const reloadVersionData = async () => {
    try {
        const res = await getVersion(balanceId.value);
        const dataBlock = parseVersionPayload(res);
        typeData.value = normalizeTypeData(dataBlock?.type_data);
        rebuildGrouped();
        try {
            const typeIds = new Set();
            (typeData.value || []).forEach((d) => {
                if (d?.type_id != null) typeIds.add(Number(d.type_id));
            });
            const typeIdsArray = Array.from(typeIds);
            if (typeIdsArray.length > 0) {
                // 使用批次 API 優化載入
                await ensureOptionsBatch(typeIdsArray);
            }
        } catch (e) {
            console.warn('重新載入選項失敗:', e);
        }
        try {
            await preloadAllFieldOptions();
        } catch {}
        // 同步摘要
        try {
            await loadSummary();
        } catch {}
    } catch (e) {
        console.error('reloadVersionData error', e);
    }
};

// 缺漏功能已停用

// 總結/完成
const summary = ref(null);
const loadingSummary = ref(false);
const finishLoading = ref(false);
const versionSource = ref(0);
/** 版本狀態：0／編輯中、1／完成 */
const versionSheetStatus = ref(0);
const isVersionFinished = computed(() => {
    const s = versionSheetStatus.value;
    return s === 1 || s === '1' || s === '完成';
});
// 版本來源：0=學員自行上傳, 1=盤點人員, 2=諮詢師
const versionSourceLabel = computed(() => {
    const map = {
        0: '自行上傳',
        1: '盤點版本',
        2: '諮詢版本'
    };
    return map[Number(versionSource.value)] || '—';
});

// 總結頁面折疊狀態（預設摺疊）
const assetDetailsCollapsed = ref(true);
const liabilityDetailsCollapsed = ref(true);

// 載入所有欄位定義，用於總覽頁面顯示
const loadAllFields = async () => {
    if (allFields.value.length) return;
    try {
        let allFieldsData = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
            const res = await getFields({
                page: page,
                page_size: 100 // 最大限制
            });

            // getFields 直接返回資料陣列，不是包裝物件
            if (Array.isArray(res)) {
                allFieldsData = allFieldsData.concat(res);
                hasMore = res.length === 100; // 如果返回滿100個，可能還有更多
                page++;
            } else if (res?.status === 1 && res.data?.items) {
                allFieldsData = allFieldsData.concat(res.data.items);
                hasMore = res.data.items.length === 100;
                page++;
            } else {
                hasMore = false;
            }
        }

        allFields.value = allFieldsData;
    } catch (e) {
        console.error('loadAllFields error', e);
    }
};

const loadSummary = async () => {
    if (!isEdit.value) return;
    try {
        loadingSummary.value = true;
        // 先載入所有欄位定義
        await loadAllFields();
        // 再載入總覽資料
        const res = await getVersionSummary(balanceId.value);
        if (res?.status === 1) {
            summary.value = res.data;
            syncSummaryNamesFromForm();
        }
    } catch (e) {
        console.error('loadSummary error', e);
    } finally {
        loadingSummary.value = false;
    }
};

const syncSummaryNamesFromForm = () => {
    if (!summary.value) return;
    const ext = versionInfoForm.value.external_name?.trim();
    const internal = versionInfoForm.value.internal_name?.trim();
    if (!ext && !internal) return;
    summary.value = {
        ...summary.value,
        ...(ext ? { external_name: ext } : {}),
        ...(internal ? { internal_name: internal } : {})
    };
};

// 總結視圖資料（美化用）；版本名稱優先使用表單最新值（避免總結 API 未刷新）
const summaryDisplayName = computed(() => {
    const ext = versionInfoForm.value.external_name?.trim();
    if (isConsultantWorkspaceRoute.value) {
        if (ext) return ext;
        const s = summary.value;
        return s?.external_name?.trim() || s?.internal_name?.trim() || '未命名版本';
    }
    const internal = versionInfoForm.value.internal_name?.trim();
    if (ext) return ext;
    if (internal) return internal;
    const s = summary.value;
    return s?.external_name?.trim() || s?.internal_name?.trim() || '';
});

const consultationBookingBanner = computed(() => {
    const b = summary.value?.consultation_booking;
    if (!b?.starts_at) return '';
    const name = String(b.consultant_name || b.consultant_core_member_id || '').trim();
    const when = formatDateTime(b.starts_at);
    const statusMap = {
        confirmed: '已預約',
        completed: '已完成',
        cancelled: '已取消',
        no_show: '未到'
    };
    const status = statusMap[b.status] || b.status || '';
    const mode = b.mode === 'offline' ? '實體' : b.mode === 'online' ? '線上' : '';
    return [name, when, mode, status].filter(Boolean).join(' · ');
});

const goConsultationFromEditor = () => {
    const b = summary.value?.consultation_booking;
    if (!b?.id || !balanceId.value) return;
    router.push({
        name: 'Consultations',
        query: {
            version_id: String(balanceId.value),
            consultation_id: String(b.id),
            ...(versionUserId.value
                ? { user_id: String(versionUserId.value) }
                : {})
        }
    });
};

watch(
    () => [
        versionInfoForm.value.external_name,
        versionInfoForm.value.internal_name
    ],
    () => syncSummaryNamesFromForm()
);

const summaryTotals = computed(() => ({
    name: summaryDisplayName.value,
    totalAssets: Number(summary.value?.total_assets ?? 0),
    totalLiabilities: Number(summary.value?.total_liabilities ?? 0),
    netAssetValue: Number(summary.value?.net_asset_value ?? 0),
    monthlyIncome: Number(summary.value?.total_monthly_income ?? 0),
    monthlyExpenses: Number(summary.value?.total_monthly_expenses ?? 0),
    monthlyCashFlow: Number(summary.value?.monthly_cash_flow ?? 0)
}));

const assetGroups = computed(() => {
    const g = summary.value?.type_data?.asset || {};
    return Object.values(g);
});
const liabilityGroups = computed(() => {
    const g = summary.value?.type_data?.liability || {};
    return Object.values(g);
});

const formatCurrency = (n) => {
    const num = Number(n || 0);
    return num.toLocaleString('zh-TW', { maximumFractionDigits: 2 });
};

// 格式化貨幣，0值顯示為-
const formatCurrencyWithDash = (n) => {
    const num = Number(n || 0);
    if (num === 0) return '-';
    return num.toLocaleString('zh-TW', { maximumFractionDigits: 2 });
};

const handleEditorExport = async () => {
    if (!balanceId.value || exportLoading.value) return;
    exportLoading.value = true;
    try {
        const blob = await exportVersion(balanceId.value);
        // API 可能回 JSON 錯誤（blob），需辨識
        if (blob?.type && String(blob.type).includes('application/json')) {
            const text = await blob.text();
            const parsed = JSON.parse(text || '{}');
            throw new Error(parsed.message || '匯出失敗');
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `資產負債表_${
            versionInfoForm.value.internal_name ||
            versionInfoForm.value.external_name ||
            balanceId.value
        }.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success('匯出成功（僅含已完成的原始／盤點／諮詢版）');
    } catch (error) {
        console.error('Export Version Error:', error);
        message.error(error?.message || '匯出失敗，請重試');
    } finally {
        exportLoading.value = false;
    }
};

// 取得欄位顯示值：若為 array 型（値是多個 id），嘗試用 detailOptionsMap 對應成中文名稱
const getFieldDisplayValue = (field) => {
    const raw = field?.value;
    const fieldId = Number(field?.field_id || field?.id);
    const detailId = Number(field?.field_id);
    const options = detailOptionsMap.value?.[fieldId] || [];

    // 檢查是否為布林欄位
    // 1. 先檢查欄位本身的 type 或 input_type
    // 2. 如果沒有，從 modalFields 查找
    // 3. 如果還是沒有，從 fields 全域資料查找
    const fieldDefinition = modalFields.value.find(
        (f) => f.id === fieldId || f.field_id === fieldId
    );

    // 如果 modalFields 找不到，從全域 allFields 資料查找
    const globalFieldDefinition =
        !fieldDefinition &&
        allFields.value.find((f) => f.id === fieldId || f.field_id === fieldId);

    const isBooleanField =
        field?.type === 'boolean' ||
        field?.input_type === 'boolean' ||
        fieldDefinition?.type === 'boolean' ||
        fieldDefinition?.input_type === 'boolean' ||
        globalFieldDefinition?.type === 'boolean' ||
        globalFieldDefinition?.input_type === 'boolean';

    if (isBooleanField) {
        if (raw === 1 || raw === '1') return '是';
        if (raw === 0 || raw === '0') return '否';
        return raw ?? '';
    }

    // 僅使用既有快取；不在渲染期間觸發 API，避免重複請求風暴
    if (!options.length) return raw ?? '';
    const toList = (v) => {
        if (Array.isArray(v)) return v.map((x) => String(x));
        if (typeof v === 'string')
            return v
                .split(',')
                .map((x) => x.trim())
                .filter((x) => x !== '');
        if (v == null) return [];
        return [String(v)];
    };
    // 若後端已提供 detail_id，優先以它對應中文
    if (!Number.isNaN(detailId) && detailId) {
        const hit = options.find((o) => String(o.value) === String(detailId));
        if (hit?.name) return hit.name;
    }
    const values = toList(raw);
    if (values.length === 0) return '';
    const labels = values.map(
        (val) =>
            options.find(
                (o) =>
                    String(o.value) === String(val) ||
                    String(o.label) === String(val)
            )?.label || String(val)
    );
    return labels.join('、');
};

const renderFieldChips = (fields) => {
    if (!Array.isArray(fields) || fields.length === 0) return [];
    return fields.map((f) => {
        const fieldName = f.field_name ?? f.name ?? f.field_id;
        const fieldValue = getFieldDisplayValue(f);
        // 如果字段名稱包含問號，就不顯示冒號
        const separator = fieldName && fieldName.includes('?') ? ' ' : ': ';
        return `${fieldName}${separator}${fieldValue}`;
    });
};

// 缺漏項顯示標籤（不顯示 liability 前綴，僅顯示名稱）
const missingLabel = (m) => {
    if (!m) return '';
    return (
        m.missing_name ||
        m.missing_option_name ||
        String(m.missing_option_id || '')
    );
};

/** 諮詢盤點：僅儲存版本／個人資訊，不結束諮詢、不鎖定版本 */
const handleConsultationSave = async () => {
    if (!isEdit.value) return;
    finishLoading.value = true;
    try {
        const versionResult = await saveVersionInfo({ silent: true });
        const udvResult = await saveUdv({ silent: true });
        if (versionResult?.ok === false || udvResult?.ok === false) {
            return;
        }
        try {
            await loadSummary();
        } catch {}
        const hadChanges =
            versionResult?.skipped === false || udvResult?.skipped === false;
        message.success(
            hadChanges ? '已儲存，可繼續編輯' : '已儲存（資料已是最新，可繼續編輯）',
        );
    } catch (e) {
        console.error('consultation save error', e);
        message.error('儲存失敗');
    } finally {
        finishLoading.value = false;
    }
};

const showConsultationSaveConfirm = () => {
    const label = Number(versionSource.value) === 2 ? '諮詢版' : '盤點版';
    Modal.confirm({
        title: `儲存${label}`,
        icon: null,
        content: h('div', { class: 'consultation-save-modal__content' }, [
            h('p', { class: 'consultation-save-modal__main' }, `保存${label}名稱與個人資訊。`),
            h('p', { class: 'consultation-save-modal__sub' }, '版本仍為「編輯中」，可繼續修改；定稿請按「完成版本」。'),
        ]),
        okText: '儲存',
        cancelText: '取消',
        okType: 'primary',
        maskClosable: true,
        centered: true,
        width: 380,
        class: 'finish-confirm-modal consultation-save-modal',
        onOk: handleConsultationSave,
    });
};

/** 諮詢盤點定稿：標記版本為「完成」，不結束諮詢 */
const handleConsultationFinishVersion = async () => {
    if (!isEdit.value || isVersionFinished.value) return;
    finishLoading.value = true;
    try {
        const versionResult = await saveVersionInfo({ silent: true });
        const udvResult = await saveUdv({ silent: true });
        if (versionResult?.ok === false || udvResult?.ok === false) {
            return;
        }
        const res = await finishVersion(balanceId.value, 0);
        if (res?.status !== 1) {
            message.error(res?.message || '完成版本失敗');
            return;
        }
        versionSheetStatus.value = 1;
        message.success('版本已標記為完成');
        await router.replace({ name: route.name, params: { ...route.params }, query: { ...route.query } });
    } catch (e) {
        console.error('consultation finish version error', e);
        message.error('完成版本失敗');
    } finally {
        finishLoading.value = false;
    }
};

const showConsultationFinishVersionConfirm = () => {
    const label = Number(versionSource.value) === 2 ? '諮詢版' : '盤點版';
    Modal.confirm({
        title: '完成版本',
        icon: null,
        content: h('div', { class: 'consultation-save-modal__content' }, [
            h('p', { class: 'consultation-save-modal__main' }, `將此${label}標記為「完成」。`),
            h('p', { class: 'consultation-save-modal__sub' }, '完成後無法再編輯；不會結束本次諮詢。'),
        ]),
        okText: '完成版本',
        cancelText: '取消',
        okType: 'primary',
        maskClosable: true,
        centered: true,
        width: 380,
        class: 'finish-confirm-modal consultation-save-modal',
        onOk: handleConsultationFinishVersion,
    });
};

const handleFinish = async () => {
    if (!isEdit.value) return;
    finishLoading.value = true;
    try {
        const bookingParam =
            versionSource.value === 0
                ? versionInfoForm.value.requires_booking
                    ? 1
                    : 0
                : 0;
        let res;
        if (isAdminFlatEditor.value) {
            const prepared = flatSheetRef.value?.prepareSave();
            if (!prepared?.ok) {
                message.error(prepared?.message || '請先完成表格中標示的必填資料');
                return;
            }
            if (!prepared.payload.batches.some(({ data }) => data.length > 0)) {
                message.error('至少需要填寫一筆資產或負債資料才能完成');
                return;
            }
            res = await saveVersionWorkspace(balanceId.value, {
                finish: true,
                requires_booking: Boolean(bookingParam),
                types: workspaceTypes(prepared.payload.batches)
            });
        } else {
            res = await finishVersion(balanceId.value, bookingParam);
        }
        if (res?.status !== 1) {
            message.error(res?.message || '完成失敗');
            return;
        }

        if (isAdminFlatEditor.value && versionSource.value === 1) {
            await copyVersion(balanceId.value, {
                source: 2,
                external_name: '諮詢版',
                internal_name: '諮詢版',
                is_visible_user: 1
            });
            const refreshed = parseVersionPayload(await getVersion(balanceId.value));
            const consultationId = Number(refreshed?.workflow?.consultation?.id || 0);
            message.success('盤點版已鎖定，諮詢版已自動建立');
            if (consultationId) {
                await router.replace(
                    buildConsultationVersionEditLocation(
                        route.name,
                        route.query,
                        consultationId
                    )
                );
                await nextTick();
                await loadVersionForEdit();
            }
            return;
        }

        if (isConsultationMode.value && Number(versionSource.value) === 2) {
            const completion = await completeConsultationBooking(consultationId.value, {
                new_version_id: balanceId.value
            });
            if (completion?.status !== 1) {
                message.error(completion?.message || '諮詢版已完成，但預約狀態同步失敗');
                return;
            }
            message.success('諮詢版本已完成，進入結論');
            await router.replace(
                buildConsultationConclusionLocation(
                    versionUserId.value || route.query.user_id,
                    balanceId.value,
                    consultationId.value
                )
            );
            return;
        }

        message.success({
            content: '財務規劃完成！版本已成功生成',
            duration: 3,
        });
        setTimeout(() => {
            router.replace('/finance-version-list');
        }, 1500);
    } catch (e) {
        console.error('finish error', e);
        message.error('完成失敗');
    } finally {
        finishLoading.value = false;
    }
};

// 顯示完成確認對話框（諮詢模式改為僅儲存）
const showFinishConfirm = () => {
    if (isConsultationMode.value) {
        showConsultationSaveConfirm();
        return;
    }
    Modal.confirm({
        title: '完成資產負債表',
        content: h('div', {}, [
            '確定要完成資產負債表嗎？',
            h('br'),
            '完成後將無法再進行編輯。',
        ]),
        okText: '確認',
        cancelText: '取消',
        okType: 'primary',
        maskClosable: true,
        icon: h(
            'div',
            {
                class: 'w-12 h-12 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center mx-auto mb-4'
            },
            [h('span', { class: 'text-white text-2xl' }, '✓')]
        ),
        centered: true,
        width: 420,
        class: 'finish-confirm-modal',
        onOk: handleFinish
    });
};

// 新增工具方法（步驟切換）
const toStep = (n) => {
    currentStep.value = n;
};
// 合併儲存和下一步功能
const saveAndNextToVersionInfo = async () => {
    // 檢查資料是否有變更
    if (hasUdvDataChanged()) {
        // 有變更則先儲存
        await saveUdv();
    }

    // 跳轉到版本資訊頁面
    currentStep.value = 0;
};

const saveAndNextToAsset = async () => {
    // 檢查資料是否有變更
    if (hasUdvDataChanged()) {
        // 有變更則先儲存
        await saveUdv();
    }

    // 跳轉到資產頁面
    currentStep.value = 2;
    if (!isReadonly.value) {
        try {
            loadMissing(0);
        } catch {}
    }
};

const nextToVersionInfo = () => {
    currentStep.value = 0;
};

const nextToPersonalInfo = () => {
    currentStep.value = 1;
};

const nextToAsset = () => {
    currentStep.value = 2;
    if (!isReadonly.value) {
        try {
            loadMissing(0);
        } catch {}
    }
};
const nextToLiability = () => {
    currentStep.value = 3;
    if (!isReadonly.value) {
        try {
            loadMissing(1);
        } catch {}
    }
};
const nextToSummary = () => {
    currentStep.value = 4;
    syncSummaryNamesFromForm();
    loadSummary();
    collapseSidebar();
};

// 合併儲存和下一步功能（版本資訊）
const saveAndNextToPersonalInfo = async () => {
    // 檢查資料是否有變更
    if (hasVersionInfoChanged()) {
        // 有變更則先儲存
        await saveVersionInfo();
    }

    // 跳轉到個人資訊頁面
    currentStep.value = 1;
};

// 檢查版本資訊是否有差異
const hasVersionInfoChanged = () => {
    if (!originalVersionInfo.value) return true; // 如果沒有原始資料，視為有變更

    const current = {
        external_name: versionInfoForm.value.external_name.trim(),
        internal_name: versionInfoForm.value.internal_name.trim(),
        requires_booking: versionInfoForm.value.requires_booking
    };

    return (
        JSON.stringify(current) !== JSON.stringify(originalVersionInfo.value)
    );
};

// 原始版本資訊快照
const originalVersionInfo = ref(null);

// 儲存原始版本資訊快照
const saveOriginalVersionInfo = () => {
    originalVersionInfo.value = {
        external_name: versionInfoForm.value.external_name.trim(),
        internal_name: versionInfoForm.value.internal_name.trim(),
        requires_booking: versionInfoForm.value.requires_booking
    };
};

// 統一錯誤處理函數
const handleVersionApiError = (error, defaultMessage = '更新失敗') => {
    // 處理驗證錯誤
    if (
        error?.response?.data?.errors &&
        typeof error.response.data.errors === 'object'
    ) {
        const errorMessages = Object.values(error.response.data.errors)
            .flat()
            .join('；');
        return message.error(
            errorMessages || error?.response?.data?.message || defaultMessage
        );
    }
    // 處理一般錯誤
    if (error?.errors && typeof error.errors === 'object') {
        const errorMessages = Object.values(error.errors)
            .flat()
            .join('；');
        return message.error(
            errorMessages || error?.message || defaultMessage
        );
    }
    // 回傳錯誤訊息
    return message.error(
        error?.response?.data?.message ||
            error?.message ||
            error?.data?.message ||
            defaultMessage
    );
};

// 儲存版本資訊
const saveVersionInfo = async (options = {}) => {
    const { silent = false } = options;
    if (!balanceId.value) {
        if (!silent) message.warning('找不到版本 ID');
        return { ok: false, skipped: true };
    }

    if (!hasVersionInfoChanged()) {
        if (!silent) message.info('沒有變更需要儲存');
        return { ok: true, skipped: true };
    }

    // 驗證必填欄位
    if (
        !versionInfoForm.value.external_name?.trim() &&
        !versionInfoForm.value.internal_name?.trim()
    ) {
        if (!silent) message.warning('對外名稱或對內名稱至少需填寫一項');
        return { ok: false, skipped: false };
    }

    try {
        versionInfoLoading.value = true;
        const externalName = versionInfoForm.value.external_name.trim();
        const internalName = versionInfoForm.value.internal_name.trim();
        const res = await updateVersion(balanceId.value, {
            external_name: externalName,
            internal_name: internalName,
            requires_booking: versionInfoForm.value.requires_booking
        });
        if (res?.status === 1) {
            if (!silent) message.success('已更新版本資訊');
            if (summary.value) {
                summary.value = {
                    ...summary.value,
                    external_name: externalName,
                    internal_name: internalName
                };
            }
            saveOriginalVersionInfo();
            return { ok: true, skipped: false };
        }
        if (!silent) handleVersionApiError(res, '更新失敗');
        return { ok: false, skipped: false };
    } catch (e) {
        console.error('update version info error', e);
        if (!silent) handleVersionApiError(e, '更新失敗');
        return { ok: false, skipped: false };
    } finally {
        versionInfoLoading.value = false;
    }
};

const saveAdminProfile = async () => {
    const versionResult = await saveVersionInfo({ silent: true });
    if (!versionResult.ok) {
        if (versionResult.skipped === false) return;
    }
    const udvResult = await saveUdv({ silent: true });
    if (!udvResult.ok && !udvResult.skipped) return;

    if (versionResult.skipped && udvResult.skipped) {
        message.info('沒有變更需要儲存');
        return;
    }
    message.success('已儲存基本資料');
};

// 收起側邊選單的函數
const collapseSidebar = () => {
    // 透過事件或直接操作 DOM 來收起側邊選單
    const event = new CustomEvent('collapse-sidebar');
    window.dispatchEvent(event);
};

// 點擊步驟切換（編輯模式）
const onEditStepChange = (idx) => {
    currentStep.value = idx;
    if (idx === 0) {
        // 版本資訊步驟無需特別處理
    } else if (idx === 2 && !isReadonly.value) {
        try {
            loadMissing(0);
        } catch {}
    } else if (idx === 3 && !isReadonly.value) {
        try {
            loadMissing(1);
        } catch {}
    } else if (idx === 4) {
        syncSummaryNamesFromForm();
        loadSummary();
        collapseSidebar();
    }
};

// 點擊步驟切換（新增模式，僅切換視圖；目前本頁主要內容在編輯模式）
const onNewStepChange = (idx) => {
    currentStep.value = idx;
};

// 類型序列工具
const getOrderedTypeIdsForClass = (cls) => {
    return types.value
        .filter((t) => typeIdToClass.value[t.id] === cls)
        .map((t) => Number(t.id));
};

const hasNextType = (cls) => {
    const order = getOrderedTypeIdsForClass(cls);
    const currentId =
        cls === 0
            ? Number(selectedAssetTypeId.value)
            : Number(selectedLiabilityTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    return idx >= 0 && idx < order.length - 1;
};

// 取得是否有上一個類型
const hasPrevType = (cls) => {
    const order = getOrderedTypeIdsForClass(cls);
    const currentId =
        cls === 0
            ? Number(selectedAssetTypeId.value)
            : Number(selectedLiabilityTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    return idx > 0;
};

const goToNextAssetTypeOrStep = () => {
    const order = getOrderedTypeIdsForClass(0);
    if (order.length === 0) {
        nextToLiability();
        return;
    }
    const currentId = Number(selectedAssetTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    if (idx === -1) {
        selectedAssetTypeId.value = order[0];
        return;
    }
    if (idx < order.length - 1) {
        selectedAssetTypeId.value = order[idx + 1];
        return;
    }
    nextToLiability();
};

const goToNextLiabilityTypeOrStep = () => {
    const order = getOrderedTypeIdsForClass(1);
    if (order.length === 0) {
        nextToSummary();
        return;
    }
    const currentId = Number(selectedLiabilityTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    if (idx === -1) {
        selectedLiabilityTypeId.value = order[0];
        return;
    }
    if (idx < order.length - 1) {
        selectedLiabilityTypeId.value = order[idx + 1];
        return;
    }
    nextToSummary();
};

// 返回上一個資產類型或返回上一步
const goToPrevAssetTypeOrStep = () => {
    const order = getOrderedTypeIdsForClass(0);
    if (order.length === 0) {
        toStep(0);
        return;
    }
    const currentId = Number(selectedAssetTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    if (idx > 0) {
        selectedAssetTypeId.value = order[idx - 1];
        return;
    }
    toStep(0);
};

// 返回上一個負債類型或返回上一步
const goToPrevLiabilityTypeOrStep = () => {
    const order = getOrderedTypeIdsForClass(1);
    if (order.length === 0) {
        toStep(1);
        return;
    }
    const currentId = Number(selectedLiabilityTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    if (idx > 0) {
        selectedLiabilityTypeId.value = order[idx - 1];
        return;
    }
    toStep(1);
};

// 從總結返回：優先回上一個負債類型，否則回到負債步驟
const goBackFromSummary = () => {
    const order = getOrderedTypeIdsForClass(1);
    const currentId = Number(selectedLiabilityTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    if (idx > 0) {
        selectedLiabilityTypeId.value = order[idx - 1];
    }
    toStep(2);
};

// 動態下一步按鈕文字（顯示下一個類型名稱或下一步）
const getTypeNameById = (id) => {
    const t = types.value.find((x) => Number(x.id) === Number(id));
    return t?.name || `類型#${id}`;
};

const nextAssetTypeId = computed(() => {
    const order = getOrderedTypeIdsForClass(0);
    const currentId = Number(selectedAssetTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    if (idx >= 0 && idx < order.length - 1) return order[idx + 1];
    return null;
});

const nextLiabilityTypeId = computed(() => {
    const order = getOrderedTypeIdsForClass(1);
    const currentId = Number(selectedLiabilityTypeId.value);
    const idx = order.findIndex((id) => id === currentId);
    if (idx >= 0 && idx < order.length - 1) return order[idx + 1];
    return null;
});

const nextAssetButtonText = computed(() => {
    if (nextAssetTypeId.value != null)
        return `下一個類型：${getTypeNameById(nextAssetTypeId.value)}`;
    return '下一步：負債';
});

const nextLiabilityButtonText = computed(() => {
    if (nextLiabilityTypeId.value != null)
        return `下一個類型：${getTypeNameById(nextLiabilityTypeId.value)}`;
    return '下一步：總結';
});
const nextSummaryButtonText = computed(() => {
    return '下一步：版本資訊';
});

// 返回工作台／場次／列表：版本常以新分頁開啟，history.back() 不可靠，改走明確目標
const resolveWorkspaceUserId = () => {
    const fromVersion = Number(versionUserId.value || 0);
    if (Number.isFinite(fromVersion) && fromVersion > 0) return fromVersion;
    const fromQuery = Number(route.query.user_id || 0);
    if (Number.isFinite(fromQuery) && fromQuery > 0) return fromQuery;
    return null;
};

const goBack = () => {
    try {
        if (isConsultantWorkspaceRoute.value) {
            const userId = resolveWorkspaceUserId();
            if (userId) {
                router.replace({
                    name: 'ConsultationWorkspaceClientDetail',
                    params: { userId: String(userId) },
                    query: consultationId.value
                        ? { consultation_id: consultationId.value }
                        : {}
                });
                return;
            }
            router.replace({ name: 'ConsultationWorkspaceBookings' });
            return;
        }
        if (window.history.length > 1) {
            router.back();
            return;
        }
        router.replace('/finance-version');
    } catch (e) {
        if (isConsultantWorkspaceRoute.value) {
            router.replace({ name: 'ConsultationWorkspaceBookings' });
        } else {
            router.replace('/finance-version');
        }
    }
};

const editorBackLabel = computed(() => {
    if (isConsultantWorkspaceRoute.value) {
        return consultationId.value ? '場次' : '工作台';
    }
    return '資產負債版本';
});

const editorPageTitle = computed(() => {
    if (!isEdit.value) return '新增資產負債表';
    return isReadonly.value ? '資產負債表查看' : '資產負債表編輯';
});

const flushConsultantMeta = async () => {
    if (isReadonly.value) return;
    try {
        if (hasVersionInfoChanged()) await saveVersionInfo();
        if (hasUdvDataChanged()) await saveUdv();
    } catch (e) {
        console.error('flushConsultantMeta error', e);
    }
};

// 新增前選擇類型（諮詢一頁式／總結頁，列表已顯示全部類型時使用）
const summaryTypeModalVisible = ref(false);
const summaryTypeModalClass = ref(0); // 0 資產 1 負債
const summarySelectedTypeId = ref(null);

const openAddAssetForSummary = () => {
    summaryTypeModalClass.value = 0;
    summarySelectedTypeId.value = null;
    summaryTypeModalVisible.value = true;
};

const openAddLiabilityForSummary = () => {
    summaryTypeModalClass.value = 1;
    summarySelectedTypeId.value = null;
    summaryTypeModalVisible.value = true;
};

const openAddAsset = () => {
    if (isConsultantOnePage.value) {
        openAddAssetForSummary();
        return;
    }
    openAddModal(0);
};

const openAddLiability = () => {
    if (isConsultantOnePage.value) {
        openAddLiabilityForSummary();
        return;
    }
    openAddModal(1);
};

const confirmSummaryTypeSelection = async () => {
    if (!summarySelectedTypeId.value) {
        message.warning('請選擇類型');
        return;
    }
    summaryTypeModalVisible.value = false;
    await openAddModal(
        summaryTypeModalClass.value,
        summarySelectedTypeId.value
    );
};

const summaryTypeOptions = computed(() => {
    const classNum = summaryTypeModalClass.value;
    return types.value
        .filter((t) => typeIdToClass.value[t.id] === classNum)
        .map((t) => ({
            value: t.id,
            label: t.name || `類型#${t.id}`
        }));
});

provide('versionEditor', {
    versionInfoForm,
    udvForm,
    summaryTotals,
    summaryDisplayName,
    formatCurrencyWithDash,
    formatCurrency,
    types,
    typeIdToClass,
    assetActiveKey,
    liabilityActiveKey,
    selectedAssetTypeId,
    selectedLiabilityTypeId,
    assetRows,
    liabilityRows,
    isReadonly,
    finishLoading,
    loadingSummary,
    versionInfoLoading,
    udvLoading,
    isConsultationMode,
    isVersionFinished,
    getOptionName,
    getTypeNameById,
    renderFieldChips,
    openAddAsset,
    openAddLiability,
    openEditModal,
    openViewItemModal,
    handleDeleteRow,
    showFinishConfirm,
    showConsultationSaveConfirm,
    showConsultationFinishVersionConfirm,
    goBack,
    flushConsultantMeta,
    saveVersionInfo,
    saveUdv,
    versionWorkflowSteps,
    showConsultantWorkflowBanner,
    versionWorkflowCurrent,
    changeVersionWorkflowStep,
    openConclusionInline,
    showConclusion
});

onMounted(async () => {
    if (isEdit.value) {
        await alignConsultationEditRoute();
        currentStep.value = 0;
        showConclusion.value = false;
        await loadTypes();
        await loadVersionForEdit();
        if (isAdminFlatEditor.value) {
            try {
                await ensureOptionsBatch(types.value.map((type) => Number(type.id)));
            } catch {}
            await preloadFlatFieldDefinitions();
            try {
                const relationRes = await getOptionRelations({ page: 1, page_size: 1000 });
                const relationList = relationRes?.data || relationRes || [];
                flatRelations.value = Array.isArray(relationList) ? relationList : [];
            } catch {
                flatRelations.value = [];
            }
            collapseSidebar();
        }
        try {
            await loadSummary();
        } catch {}
        try {
            await loadMissing(0);
        } catch {}
        if (isConsultantOnePage.value) {
            collapseSidebar();
            try {
                await loadMissing(1);
            } catch {}
        }
    } else {
        await searchUsers('');
    }
});

// 在流程導覽切換同一個元件的不同版本時，Vue 會重用元件實例；
// 需重新載入版本資料，否則上方「目前版本」與表格仍會停留在上一版。
watch(
    () => route.params.id,
    async (id, previousId) => {
        if (!id || String(id) === String(previousId)) return;
        currentStep.value = 0;
        showConclusion.value = false;
        versionSource.value = 0;
        versionSheetStatus.value = 0;
        versionWorkflow.value = null;
        await loadVersionForEdit();
        await loadSummary();
    }
);
</script>

<template>
    <div class="p-4" :class="{ 'admin-editor-page': isAdminFlatEditor }">
        <ConsultationConclusion v-if="showConclusion" />
        <ConsultantVersionCompactView v-else-if="showConsultantCompact" />

        <template v-else>
        <header v-if="isAdminFlatEditor" class="admin-editor-topbar">
            <div class="admin-editor-topbar__nav">
                <button type="button" class="admin-editor-topbar__back" @click="goBack">
                    <ArrowLeftOutlined />
                    {{ editorBackLabel }}
                </button>
                <span class="admin-editor-topbar__sep">/</span>
                <span class="admin-editor-topbar__title">
                    {{ editorPageTitle }}
                </span>
                <a-tag v-if="summaryDisplayName" class="admin-editor-topbar__tag">
                    {{ summaryDisplayName }}
                </a-tag>
                <button
                    v-if="consultationBookingBanner"
                    type="button"
                    class="admin-editor-topbar__booking"
                    :title="`${consultationBookingBanner}（點擊查看預約細節）`"
                    @click="goConsultationFromEditor"
                >
                    {{ consultationBookingBanner }}
                </button>
            </div>
            <a-button
                v-if="canExport"
                class="admin-editor-btn admin-editor-btn--export"
                :loading="exportLoading"
                @click="handleEditorExport"
            >
                <template #icon><DownloadOutlined /></template>
                匯出 Excel
            </a-button>
        </header>

        <FinanceStatusBanner
            v-if="isEdit && (isConsultantOnePage || isAdminFlatEditor || currentStep !== 4)"
            :totals="summaryTotals"
            :steps="showConsultantWorkflowBanner ? versionWorkflowSteps : []"
            :current-step="versionWorkflowCurrent"
            @step-change="changeVersionWorkflowStep"
        />

        <div v-if="isEdit" class="mb-4">
            <a-steps
                v-if="!isConsultantOnePage && !isAdminFlatEditor"
                :current="currentStep"
                size="small"
                @change="onEditStepChange"
                :class="['custom-steps', { 'readonly-mode': isReadonly }]"
            >
                <a-step v-for="(s, i) in editSteps" :key="i" :title="s.title" />
            </a-steps>
        </div>

        <!-- 新增模式：保留（若直接進來 create） -->
        <a-steps
            v-else
            :current="currentStep"
            size="small"
            class="mb-6 custom-steps"
            @change="onNewStepChange"
        >
            <a-step v-for="(s, i) in steps" :key="i" :title="s.title" />
        </a-steps>

        <!-- 編輯：步驟 版本資訊 -->
        <AdminEditorProfileCard
            v-if="isEdit && isAdminFlatEditor && showEditStep(0)"
            :version-info-form="versionInfoForm"
            :udv-form="udvForm"
            :version-name="summaryDisplayName"
            :version-source="Number(versionSource)"
            :is-readonly="isReadonly"
            :version-loading="versionInfoLoading"
            :personal-loading="udvLoading"
            @save="saveAdminProfile"
        />

        <a-card
            v-else-if="isEdit && showEditStep(0)"
            id="cw-section-version"
            title="版本資訊"
            class="summary-card consultant-section-card"
        >
            <div
                v-if="!isConsultantOnePage && !isAdminFlatEditor"
                class="mb-6 flex items-center justify-between"
            >
                <a-button
                    v-if="!isReadonly"
                    @click="goBack"
                    class="!border-gray-300 !text-gray-600 hover:!border-gray-400 hover:!text-gray-700"
                >
                    ← 上一步
                </a-button>
            </div>

            <a-form layout="vertical">
                <div
                    class="grid grid-cols-1 gap-6"
                    :class="{
                        'md:grid-cols-2': !isConsultantOnePage && !isAdminFlatEditor,
                        'admin-version-fields': isAdminFlatEditor
                    }"
                >
                    <a-form-item v-if="!isConsultantOnePage" label="內部名稱">
                        <a-input
                            v-model:value="versionInfoForm.internal_name"
                            :disabled="isReadonly"
                        />
                    </a-form-item>
                    <a-form-item
                        :label="isConsultantOnePage ? '版本名稱' : '外部名稱'"
                    >
                        <a-input
                            v-model:value="versionInfoForm.external_name"
                            :disabled="isReadonly"
                            placeholder="學員看到的版本名稱"
                        />
                    </a-form-item>
                    <a-form-item label="版本來源">
                        <a-input
                            :value="versionSourceLabel"
                            disabled
                        />
                    </a-form-item>
                    <a-form-item
                        v-if="!isConsultantOnePage && versionSource === 0"
                        label="需預約"
                    >
                        <a-switch
                            v-model:checked="versionInfoForm.requires_booking"
                            :disabled="isReadonly"
                        />
                        <span class="ml-2 text-gray-600">預約諮詢</span>
                    </a-form-item>
                </div>
            </a-form>

            <div
                v-if="!isConsultantOnePage && !isAdminFlatEditor"
                class="flex justify-end"
            >
                <div class="flex gap-4">
                    <a-button
                        v-if="!isReadonly"
                        @click="saveAndNextToPersonalInfo"
                        type="primary"
                        :loading="versionInfoLoading"
                        class="!bg-gradient-to-r !from-green-500 !to-green-600 hover:!from-green-600 hover:!to-green-700 !border-0 !shadow-md hover:!shadow-lg transition-all duration-200 !text-white"
                    >
                        <span v-if="hasVersionInfoChanged()">
                            儲存並前往個人資訊 →
                        </span>
                        <span v-else>前往個人資訊 →</span>
                    </a-button>

                    <a-button
                        v-if="isReadonly"
                        @click="nextToPersonalInfo"
                        type="primary"
                        class="!bg-gradient-to-r !from-green-500 !to-green-600 hover:!from-green-600 hover:!to-green-700 !border-0 !shadow-md hover:!shadow-lg transition-all duration-200 !text-white"
                    >
                        前往個人資訊 →
                    </a-button>
                </div>
            </div>
            <div
                v-else-if="!isReadonly"
                class="flex justify-end mt-2"
            >
                <a-button
                    type="primary"
                    :loading="versionInfoLoading"
                    @click="saveVersionInfo"
                >
                    {{ isAdminFlatEditor ? '儲存版本資訊' : '儲存版本名稱' }}
                </a-button>
            </div>
        </a-card>

        <!-- 編輯：步驟 個人資訊 -->
        <a-card
            v-if="isEdit && showEditStep(1) && !isAdminFlatEditor"
            id="cw-section-personal"
            title="個人資訊"
            class="personal-info-card consultant-section-card"
        >
            <a-form layout="vertical">
                <div
                    class="grid grid-cols-1 md:grid-cols-2 gap-6"
                    :class="{ 'admin-personal-fields': isAdminFlatEditor }"
                >
                    <div>
                        <a-form-item label="年齡">
                            <a-input-number
                                v-model:value="udvForm.age"
                                :min="0"
                                style="width: 160px"
                                :disabled="udvLoading || isReadonly"
                            />
                        </a-form-item>
                        <a-form-item label="職業">
                            <a-input
                                v-model:value="udvForm.job"
                                :disabled="udvLoading || isReadonly"
                            />
                        </a-form-item>
                    </div>
                    <div>
                        <a-form-item label="婚姻狀態">
                            <a-select
                                v-model:value="udvForm.marital_status"
                                style="width: 160px"
                                :disabled="udvLoading || isReadonly"
                            >
                                <a-select-option :value="0">
                                    未婚
                                </a-select-option>
                                <a-select-option :value="1">
                                    已婚
                                </a-select-option>
                            </a-select>
                        </a-form-item>
                        <a-form-item label="子女數">
                            <a-input-number
                                v-model:value="udvForm.children"
                                :min="0"
                                style="width: 160px"
                                :disabled="udvLoading || isReadonly"
                            />
                        </a-form-item>
                    </div>
                </div>
                <div
                    v-if="!isConsultantOnePage && !isAdminFlatEditor"
                    class="mt-6 flex justify-end"
                >
                    <a-button
                        @click="saveAndNextToAsset"
                        type="primary"
                        :loading="udvLoading"
                        class="!bg-gradient-to-r !from-green-500 !to-green-600 hover:!from-green-600 hover:!to-green-700 !border-0 !shadow-md hover:!shadow-lg transition-all duration-200 !text-white"
                    >
                        <span v-if="hasUdvDataChanged()">儲存並前往資產 →</span>
                        <span v-else>前往資產 →</span>
                    </a-button>
                </div>
                <div
                    v-else-if="!isReadonly"
                    class="mt-4 flex justify-end"
                >
                    <a-button
                        type="primary"
                        :loading="udvLoading"
                        @click="saveUdv"
                    >
                        儲存個人資訊
                    </a-button>
                </div>
            </a-form>
        </a-card>

        <!-- 一般後台：整張試算表直接填寫，最後依類型批次送出。 -->
        <AdminFlatBalanceSheet
            v-if="isAdminFlatEditor"
            ref="flatSheetRef"
            :types="types"
            :rows="typeData"
            :class-map="typeIdToClass"
            :options-by-type="optionListCache"
            :fields-by-option="flatFieldsByOption"
            :relations="flatRelations"
            :readonly="isReadonly"
            :seed-placeholders="!isReadonly"
            :save-label="isConsultantWorkspaceRoute ? (Number(versionSource) === 2 ? '儲存諮詢版' : '儲存盤點版') : '儲存草稿'"
            :show-save-bar="!isConsultantWorkspaceRoute && !isAdminFlatEditor"
            :save-loading="flatSaveLoading"
            @save-all="saveFlatSheet"
        />

        <WhiteboardPhotoPanel
            v-if="isAdminFlatEditor && balanceId && Number(versionSource) === 2"
            :balance-id="balanceId"
            :readonly="
                route.name === 'ConsultationWorkspaceVersionView' ||
                route.name === 'FinanceVersionView' ||
                isReadonly
            "
        />

        <AdminEditorFooter
            v-if="isAdminFlatEditor"
            :is-readonly="isReadonly"
            :is-consultation-mode="isConsultationMode"
            :version-source="Number(versionSource)"
            :finish-loading="finishLoading"
            :flat-save-loading="flatSaveLoading"
            @save-draft="saveFlatSheetFromSummary"
            @finish="handleFinish"
        />

        <!-- 編輯：步驟 資產 -->
        <a-card
            v-if="isEdit && showEditStep(2) && !isAdminFlatEditor"
            id="cw-section-assets"
            title="資產"
            class="asset-card consultant-section-card"
        >
            <!-- 類型頁籤 -->
            <div class="mb-6">
                <a-tabs v-model:activeKey="assetActiveKey" class="mb-4">
                    <a-tab-pane
                        v-for="t in types.filter(
                            (t) => typeIdToClass[t.id] === 0
                        )"
                        :key="String(t.id)"
                        :tab="t.name || '類型#' + t.id"
                    />
                </a-tabs>
            </div>

            <!-- 缺漏提示區塊（有資料才顯示） -->
            <a-card
                v-if="!isReadonly && missingAsset.list?.length > 0"
                size="small"
                class="mb-6 !bg-gradient-to-r !from-emerald-50 !to-green-50 !border-emerald-200 !shadow-sm"
            >
                <template #title>
                    <div class="flex items-center gap-2">
                        <span class="text-emerald-600 text-lg">💡</span>
                        <span class="text-emerald-800 font-semibold">
                            發現與當前資產相關的待填項
                        </span>
                    </div>
                </template>
                <div
                    v-if="missingAsset.hint"
                    class="text-emerald-700 text-sm mb-3 bg-emerald-100/50 p-2 rounded-md"
                >
                    {{ missingAsset.hint }}
                </div>
                <div class="space-y-3">
                    <div
                        class="flex items-center justify-between rounded-lg border border-emerald-200 p-3 bg-white/70 hover:bg-white/90 transition-colors"
                        v-for="m in missingAsset.list"
                        :key="`${m.missing_type_id}-${m.missing_option_id}`"
                    >
                        <div class="flex items-center gap-3">
                            <template v-if="m.is_filled !== true">
                                <a-button
                                    size="small"
                                    class="!bg-gradient-to-r !from-emerald-500 !to-green-500 !border-0 hover:!from-emerald-600 hover:!to-green-600 !text-white !shadow-md hover:!shadow-lg transition-all duration-200"
                                    @click="
                                        () => {
                                            // 優先使用 missing_type 判斷 class（明確表示是資產還是負債的建議）
                                            // missing_type: 'asset' 表示資產建議，'liability' 表示負債建議
                                            const targetClass =
                                                String(
                                                    m.missing_type
                                                ).toLowerCase() === 'liability'
                                                    ? 1
                                                    : 0;
                                            openAddForType(
                                                targetClass,
                                                m.missing_type_id,
                                                m.missing_option_id
                                            );
                                        }
                                    "
                                >
                                    {{
                                        String(m.missing_type).toLowerCase() ===
                                        'liability'
                                            ? '新增負債'
                                            : '新增資產'
                                    }}
                                </a-button>
                            </template>
                            <span
                                v-else
                                class="inline-flex items-center border border-green-300 text-green-700 bg-green-50 rounded-full px-3 py-1 text-xs font-medium select-none"
                            >
                                ✓ 新增完畢
                            </span>
                            <div>
                                <div class="font-semibold text-gray-800">
                                    {{ missingLabel(m) }}
                                </div>
                                <div
                                    v-if="m.suggestion"
                                    class="text-xs text-gray-600 mt-1"
                                >
                                    {{ m.suggestion }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </a-card>

            <!-- 資產列表 -->
            <div class="mt-6">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold text-gray-800">
                        資產列表
                    </h3>
                    <a-button
                        v-if="!isReadonly"
                        type="primary"
                        @click="openAddAsset"
                        class="asset-button !bg-gradient-to-r !from-green-500 !to-green-600 hover:!from-green-600 hover:!to-green-700 !border-0 !shadow-md hover:!shadow-lg transition-all duration-200"
                    >
                        新增資產
                    </a-button>
                </div>
                <a-table
                    :data-source="
                        assetRows.filter(
                            (r) =>
                                !selectedAssetTypeId ||
                                Number(r.type_id) ===
                                    Number(selectedAssetTypeId)
                        )
                    "
                    :pagination="false"
                    row-key="data_id"
                    v-if="types.some((t) => typeIdToClass[t.id] === 0)"
                    class="asset-table"
                    size="middle"
                    :row-class-name="
                        () => 'hover:bg-green-50/30 transition-colors'
                    "
                >
                    <a-table-column title="選項" key="option_name" width="120">
                        <template #default="{ record }">
                            <span class="font-medium text-gray-700">
                                {{
                                    record.option_name ||
                                    getOptionName(
                                        record.type_id,
                                        record.option_id,
                                        ''
                                    )
                                }}
                            </span>
                        </template>
                    </a-table-column>
                    <a-table-column
                        title="名稱"
                        dataIndex="name"
                        key="name"
                        width="120"
                    >
                        <template #default="{ record }">
                            <span class="font-medium text-gray-800">
                                {{ record.name }}
                            </span>
                        </template>
                    </a-table-column>
                    <a-table-column
                        title="市值(現值)"
                        dataIndex="amount"
                        key="amount"
                        width="140"
                    >
                        <template #default="{ record }">
                            <span class="font-semibold text-green-600 text-lg">
                                {{ formatCurrency(record.amount) }}
                            </span>
                        </template>
                    </a-table-column>
                    <a-table-column
                        title="月收入"
                        dataIndex="monthly_interest_expense"
                        key="monthly_interest_expense"
                        width="120"
                    >
                        <template #default="{ record }">
                            <span class="font-semibold text-green-500">
                                {{
                                    formatCurrency(
                                        record.monthly_interest_expense
                                    )
                                }}
                            </span>
                        </template>
                    </a-table-column>
                    <a-table-column title="詳細資料" key="details" width="200">
                        <template #default="{ record }">
                            <div class="flex flex-wrap gap-1">
                                <a-tag
                                    v-for="chip in renderFieldChips(
                                        record.fields
                                    )"
                                    :key="chip"
                                    size="small"
                                    class="asset-tag !rounded-full"
                                >
                                    {{ chip }}
                                </a-tag>
                            </div>
                        </template>
                    </a-table-column>
                    <a-table-column title="操作" key="actions">
                        <template #default="{ record }">
                            <a-space>
                                <a-button
                                    v-if="isReadonly"
                                    type="link"
                                    @click="() => openViewItemModal(record)"
                                >
                                    查看
                                </a-button>
                                <template v-if="!isReadonly">
                                    <a-button
                                        type="text"
                                        size="small"
                                        class="!p-1 !h-6 !w-6 !min-w-6"
                                        @click="() => openEditModal(0, record)"
                                        title="編輯"
                                    >
                                        <span class="text-blue-500 text-sm">
                                            ✎
                                        </span>
                                    </a-button>
                                    <a-popconfirm
                                        title="確認刪除？"
                                        ok-text="刪除"
                                        cancel-text="取消"
                                        @confirm="
                                            () => handleDeleteRow(0, record)
                                        "
                                    >
                                        <a-button
                                            type="text"
                                            size="small"
                                            class="!p-1 !h-6 !w-6 !min-w-6"
                                            title="刪除"
                                        >
                                            <span class="text-red-500 text-sm">
                                                ✕
                                            </span>
                                        </a-button>
                                    </a-popconfirm>
                                </template>
                            </a-space>
                        </template>
                    </a-table-column>
                </a-table>
                <a-empty v-else description="無資產類型" />
            </div>

            <div
                v-if="!isConsultantOnePage"
                class="mt-6 flex items-center justify-between"
            >
                <a-button
                    @click="goToPrevAssetTypeOrStep"
                    class="!border-gray-300 !text-gray-600 hover:!border-gray-400 hover:!text-gray-700"
                >
                    ← 上一步
                </a-button>
                <a-button
                    type="primary"
                    @click="goToNextAssetTypeOrStep"
                    class="!bg-gradient-to-r !from-green-500 !to-green-600 hover:!from-green-600 hover:!to-green-700 !border-0 !shadow-md hover:!shadow-lg transition-all duration-200"
                >
                    {{ nextAssetButtonText }} →
                </a-button>
            </div>
        </a-card>

        <!-- 編輯：步驟 負債 -->
        <a-card
            v-if="isEdit && showEditStep(3) && !isAdminFlatEditor"
            id="cw-section-liabilities"
            title="負債"
            class="liability-card consultant-section-card"
        >
            <div class="mb-6">
                <a-tabs v-model:activeKey="liabilityActiveKey" class="mb-4">
                    <a-tab-pane
                        v-for="t in types.filter(
                            (t) => typeIdToClass[t.id] === 1
                        )"
                        :key="String(t.id)"
                        :tab="t.name || '類型#' + t.id"
                    />
                </a-tabs>
            </div>

            <!-- 缺漏提示區塊（有資料才顯示） -->
            <a-card
                v-if="!isReadonly && missingLiability.list?.length > 0"
                size="small"
                class="mb-6 !bg-gradient-to-r !from-amber-50 !to-yellow-50 !border-amber-200 !shadow-sm"
            >
                <template #title>
                    <div class="flex items-center gap-2">
                        <span class="text-amber-600 text-lg">💡</span>
                        <span class="text-amber-800 font-semibold">
                            發現與當前負債相關的待填項
                        </span>
                    </div>
                </template>
                <div
                    v-if="missingLiability.hint"
                    class="text-amber-700 text-sm mb-3 bg-amber-100/50 p-2 rounded-md"
                >
                    {{ missingLiability.hint }}
                </div>
                <div class="space-y-3">
                    <div
                        class="flex items-center justify-between rounded-lg border border-amber-200 p-3 bg-white/70 hover:bg-white/90 transition-colors"
                        v-for="m in missingLiability.list"
                        :key="`${m.missing_type_id}-${m.missing_option_id}`"
                    >
                        <div class="flex items-center gap-3">
                            <template v-if="m.is_filled !== true">
                                <a-button
                                    size="small"
                                    class="!bg-gradient-to-r !from-amber-500 !to-orange-500 !border-0 hover:!from-amber-600 hover:!to-orange-600 !text-white !shadow-md hover:!shadow-lg transition-all duration-200"
                                    @click="
                                        () => {
                                            // 優先使用 missing_type 判斷 class（明確表示是資產還是負債的建議）
                                            // missing_type: 'asset' 表示資產建議，'liability' 表示負債建議
                                            const targetClass =
                                                String(
                                                    m.missing_type
                                                ).toLowerCase() === 'liability'
                                                    ? 1
                                                    : 0;
                                            openAddForType(
                                                targetClass,
                                                m.missing_type_id,
                                                m.missing_option_id
                                            );
                                        }
                                    "
                                >
                                    {{
                                        String(m.missing_type).toLowerCase() ===
                                        'liability'
                                            ? '新增負債'
                                            : '新增資產'
                                    }}
                                </a-button>
                            </template>
                            <span
                                v-else
                                class="inline-flex items-center border border-green-300 text-green-700 bg-green-50 rounded-full px-3 py-1 text-xs font-medium select-none"
                            >
                                ✓ 新增完畢
                            </span>
                            <div>
                                <div class="font-semibold text-gray-800">
                                    {{ missingLabel(m) }}
                                </div>
                                <div
                                    v-if="m.suggestion"
                                    class="text-xs text-gray-600 mt-1"
                                >
                                    {{ m.suggestion }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </a-card>

            <div class="mt-6">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold text-gray-800">
                        負債列表
                    </h3>
                    <a-button
                        v-if="!isReadonly"
                        type="primary"
                        @click="openAddLiability"
                        class="liability-button !bg-gradient-to-r !from-red-500 !to-red-600 hover:!from-red-600 hover:!to-red-700 !border-0 !shadow-md hover:!shadow-lg transition-all duration-200"
                    >
                        新增負債
                    </a-button>
                </div>
                <a-table
                    :data-source="
                        liabilityRows.filter(
                            (r) =>
                                !selectedLiabilityTypeId ||
                                Number(r.type_id) ===
                                    Number(selectedLiabilityTypeId)
                        )
                    "
                    :pagination="false"
                    row-key="data_id"
                    v-if="types.some((t) => typeIdToClass[t.id] === 1)"
                    class="liability-table"
                    size="middle"
                    :row-class-name="
                        () => 'hover:bg-red-50/30 transition-colors'
                    "
                >
                    <a-table-column title="選項" key="option_name" width="120">
                        <template #default="{ record }">
                            <span class="font-medium text-gray-700">
                                {{
                                    record.option_name ||
                                    getOptionName(
                                        record.type_id,
                                        record.option_id,
                                        ''
                                    )
                                }}
                            </span>
                        </template>
                    </a-table-column>
                    <a-table-column
                        title="名稱"
                        dataIndex="name"
                        key="name"
                        width="120"
                    >
                        <template #default="{ record }">
                            <span class="font-medium text-gray-800">
                                {{ record.name }}
                            </span>
                        </template>
                    </a-table-column>
                    <a-table-column
                        title="餘額"
                        dataIndex="amount"
                        key="amount"
                        width="140"
                    >
                        <template #default="{ record }">
                            <span class="font-semibold text-red-600 text-lg">
                                {{ formatCurrency(record.amount) }}
                            </span>
                        </template>
                    </a-table-column>
                    <a-table-column
                        title="月付金"
                        dataIndex="monthly_interest_expense"
                        key="monthly_interest_expense"
                        width="120"
                    >
                        <template #default="{ record }">
                            <span class="font-semibold text-red-500">
                                {{
                                    formatCurrency(
                                        record.monthly_interest_expense
                                    )
                                }}
                            </span>
                        </template>
                    </a-table-column>
                    <a-table-column title="詳細資料" key="details" width="200">
                        <template #default="{ record }">
                            <div class="flex flex-wrap gap-1">
                                <a-tag
                                    v-for="chip in renderFieldChips(
                                        record.fields
                                    )"
                                    :key="chip"
                                    size="small"
                                    class="liability-tag !rounded-full"
                                >
                                    {{ chip }}
                                </a-tag>
                            </div>
                        </template>
                    </a-table-column>
                    <a-table-column title="操作" key="actions">
                        <template #default="{ record }">
                            <a-space>
                                <a-button
                                    v-if="isReadonly"
                                    type="link"
                                    @click="() => openViewItemModal(record)"
                                >
                                    查看
                                </a-button>
                                <template v-if="!isReadonly">
                                    <a-button
                                        type="text"
                                        size="small"
                                        class="!p-1 !h-6 !w-6 !min-w-6"
                                        @click="() => openEditModal(1, record)"
                                        title="編輯"
                                    >
                                        <span class="text-blue-500 text-sm">
                                            ✎
                                        </span>
                                    </a-button>
                                    <a-popconfirm
                                        title="確認刪除？"
                                        ok-text="刪除"
                                        cancel-text="取消"
                                        @confirm="
                                            () => handleDeleteRow(1, record)
                                        "
                                    >
                                        <a-button
                                            type="text"
                                            size="small"
                                            class="!p-1 !h-6 !w-6 !min-w-6"
                                            title="刪除"
                                        >
                                            <span class="text-red-500 text-sm">
                                                ✕
                                            </span>
                                        </a-button>
                                    </a-popconfirm>
                                </template>
                            </a-space>
                        </template>
                    </a-table-column>
                </a-table>
                <a-empty v-else description="無負債類型" />
            </div>

            <div class="mt-6 flex items-center justify-between">
                <a-button
                    @click="goToPrevLiabilityTypeOrStep"
                    class="!border-gray-300 !text-gray-600 hover:!border-gray-400 hover:!text-gray-700"
                >
                    ← 上一步
                </a-button>
                <a-button
                    type="primary"
                    @click="goToNextLiabilityTypeOrStep"
                    class="!bg-gradient-to-r !from-red-500 !to-red-600 hover:!from-red-600 hover:!to-red-700 !border-0 !shadow-md hover:!shadow-lg transition-all duration-200"
                >
                    {{ nextLiabilityButtonText }} →
                </a-button>
            </div>
        </a-card>

        <!-- 編輯：步驟 總結 -->
        <a-card
            v-if="isEdit && showSummaryStep && !isAdminFlatEditor"
            id="cw-section-summary"
            :title="isAdminFlatEditor ? '完成設定' : '總結'"
            class="summary-card consultant-section-card"
        >
            <div class="mb-6 flex items-center justify-between flex-wrap gap-2">
                <a-button
                    v-if="!isConsultantOnePage && !isAdminFlatEditor"
                    @click="goBackFromSummary"
                    class="!border-gray-300 !text-gray-600 hover:!border-gray-400 hover:!text-gray-700"
                >
                    ← 上一步
                </a-button>
                <div
                    class="text-gray-600 font-medium"
                    :class="{ 'ml-auto': isConsultantOnePage }"
                >
                    版本名稱：{{ summaryTotals.name }}
                </div>
                <div
                    v-if="isAdminFlatEditor"
                    class="w-full text-sm text-gray-500"
                >
                    上方已呈現完整資產負債明細與統計；確認資料後可在此設定預約並完成版本。
                </div>
            </div>

            <!-- 資產與負債明細左右呈現 -->
            <div
                v-if="!isAdminFlatEditor"
                class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6"
            >
                <!-- 資產明細 -->
                <a-card size="small" class="bg-green-50 border-green-200">
                    <template #title>
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <a-button
                                    type="text"
                                    size="small"
                                    @click="
                                        assetDetailsCollapsed =
                                            !assetDetailsCollapsed
                                    "
                                    class="!p-1 !h-6 !w-6 !min-w-6"
                                >
                                    <span class="text-gray-500 text-sm">
                                        {{ assetDetailsCollapsed ? '▶' : '▼' }}
                                    </span>
                                </a-button>
                                <span>資產明細</span>
                            </div>
                            <a-button
                                v-if="!isReadonly"
                                type="primary"
                                size="small"
                                @click="openAddAssetForSummary"
                            >
                                新增資產
                            </a-button>
                        </div>
                    </template>
                    <div class="mt-3 space-y-3">
                        <a-card
                            v-for="g in assetGroups"
                            :key="g.type_name"
                            size="small"
                            class="bg-green-25 border-green-100"
                        >
                            <div class="font-medium mb-2">
                                {{ g.type_name }}
                            </div>
                            <a-table
                                :data-source="g.items || []"
                                :pagination="false"
                                row-key="data_id"
                                size="small"
                            >
                                <a-table-column
                                    title="選項"
                                    dataIndex="option_name"
                                    key="option_name"
                                />
                                <a-table-column
                                    title="名稱"
                                    dataIndex="name"
                                    key="name"
                                />
                                <a-table-column title="金額" key="amount">
                                    <template #default="{ record }">
                                        {{ formatCurrency(record.amount) }}
                                    </template>
                                </a-table-column>
                                <a-table-column
                                    title="月收入"
                                    key="monthly_interest_expense"
                                >
                                    <template #default="{ record }">
                                        {{
                                            formatCurrency(
                                                record.monthly_interest_expense
                                            )
                                        }}
                                    </template>
                                </a-table-column>
                                <a-table-column title="備註" key="fields">
                                    <template #default="{ record }">
                                        <a-space wrap>
                                            <a-tag
                                                v-for="chip in renderFieldChips(
                                                    record.fields
                                                )"
                                                :key="chip"
                                            >
                                                {{ chip }}
                                            </a-tag>
                                        </a-space>
                                    </template>
                                </a-table-column>
                                <a-table-column
                                    title="操作"
                                    key="actions"
                                    v-if="!isReadonly"
                                    width="80"
                                >
                                    <template #default="{ record }">
                                        <a-space size="small">
                                            <a-button
                                                type="text"
                                                size="small"
                                                class="!p-1 !h-6 !w-6 !min-w-6"
                                                @click="
                                                    () =>
                                                        openEditModal(0, record)
                                                "
                                                title="編輯"
                                            >
                                                <span
                                                    class="text-blue-500 text-sm"
                                                >
                                                    ✎
                                                </span>
                                            </a-button>
                                            <a-popconfirm
                                                title="確認刪除？"
                                                ok-text="刪除"
                                                cancel-text="取消"
                                                @confirm="
                                                    () =>
                                                        handleDeleteRow(
                                                            0,
                                                            record
                                                        )
                                                "
                                            >
                                                <a-button
                                                    type="text"
                                                    size="small"
                                                    class="!p-1 !h-6 !w-6 !min-w-6"
                                                    title="刪除"
                                                >
                                                    <span
                                                        class="text-red-500 text-sm"
                                                    >
                                                        ✕
                                                    </span>
                                                </a-button>
                                            </a-popconfirm>
                                        </a-space>
                                    </template>
                                </a-table-column>
                            </a-table>
                        </a-card>
                    </div>

                    <!-- 資產類型統計表格 -->
                    <div v-show="!assetDetailsCollapsed" class="mt-4">
                        <a-card size="small">
                            <template #title>
                                <div class="text-gray-500">資產類型統計</div>
                            </template>
                            <a-table
                                :data-source="assetGroups"
                                :pagination="false"
                                row-key="type_name"
                                size="small"
                                :row-class-name="() => ''"
                            >
                                <a-table-column
                                    title="類型"
                                    dataIndex="type_name"
                                    key="type_name"
                                >
                                    <template #default="{ record }">
                                        <span class="font-medium text-gray-800">
                                            {{ record.type_name }}
                                        </span>
                                    </template>
                                </a-table-column>
                                <a-table-column
                                    title="總金額"
                                    key="total_amount"
                                >
                                    <template #default="{ record }">
                                        <span
                                            class="font-semibold text-green-600"
                                        >
                                            {{
                                                formatCurrency(
                                                    record.total_amount
                                                )
                                            }}
                                        </span>
                                    </template>
                                </a-table-column>
                                <a-table-column
                                    title="月收入合計"
                                    key="total_monthly_interest_expense"
                                >
                                    <template #default="{ record }">
                                        <span
                                            class="font-semibold text-green-600"
                                        >
                                            {{
                                                formatCurrency(
                                                    record.total_monthly_interest_expense
                                                )
                                            }}
                                        </span>
                                    </template>
                                </a-table-column>
                            </a-table>
                        </a-card>
                    </div>
                </a-card>

                <!-- 負債明細 -->
                <a-card size="small" class="bg-red-50 border-red-200">
                    <template #title>
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <a-button
                                    type="text"
                                    size="small"
                                    @click="
                                        liabilityDetailsCollapsed =
                                            !liabilityDetailsCollapsed
                                    "
                                    class="!p-1 !h-6 !w-6 !min-w-6"
                                >
                                    <span class="text-gray-500 text-sm">
                                        {{
                                            liabilityDetailsCollapsed
                                                ? '▶'
                                                : '▼'
                                        }}
                                    </span>
                                </a-button>
                                <span>負債明細</span>
                            </div>
                            <a-button
                                v-if="!isReadonly"
                                type="primary"
                                danger
                                size="small"
                                @click="openAddLiabilityForSummary"
                            >
                                新增負債
                            </a-button>
                        </div>
                    </template>
                    <div class="mt-3 space-y-3">
                        <a-card
                            v-for="g in liabilityGroups"
                            :key="g.type_name"
                            size="small"
                            class="bg-red-25 border-red-100"
                        >
                            <div class="font-medium mb-2">
                                {{ g.type_name }}
                            </div>
                            <a-table
                                :data-source="g.items || []"
                                :pagination="false"
                                row-key="data_id"
                                size="small"
                            >
                                <a-table-column
                                    title="選項"
                                    dataIndex="option_name"
                                    key="option_name"
                                />
                                <a-table-column
                                    title="名稱"
                                    dataIndex="name"
                                    key="name"
                                />
                                <a-table-column title="金額" key="amount">
                                    <template #default="{ record }">
                                        {{ formatCurrency(record.amount) }}
                                    </template>
                                </a-table-column>
                                <a-table-column
                                    title="月支出"
                                    key="monthly_interest_expense"
                                >
                                    <template #default="{ record }">
                                        {{
                                            formatCurrency(
                                                record.monthly_interest_expense
                                            )
                                        }}
                                    </template>
                                </a-table-column>
                                <a-table-column title="備註" key="fields">
                                    <template #default="{ record }">
                                        <a-space wrap>
                                            <a-tag
                                                v-for="chip in renderFieldChips(
                                                    record.fields
                                                )"
                                                :key="chip"
                                            >
                                                {{ chip }}
                                            </a-tag>
                                        </a-space>
                                    </template>
                                </a-table-column>
                                <a-table-column
                                    title="操作"
                                    key="actions"
                                    v-if="!isReadonly"
                                    width="80"
                                >
                                    <template #default="{ record }">
                                        <a-space size="small">
                                            <a-button
                                                type="text"
                                                size="small"
                                                class="!p-1 !h-6 !w-6 !min-w-6"
                                                @click="
                                                    () =>
                                                        openEditModal(1, record)
                                                "
                                                title="編輯"
                                            >
                                                <span
                                                    class="text-blue-500 text-sm"
                                                >
                                                    ✎
                                                </span>
                                            </a-button>
                                            <a-popconfirm
                                                title="確認刪除？"
                                                ok-text="刪除"
                                                cancel-text="取消"
                                                @confirm="
                                                    () =>
                                                        handleDeleteRow(
                                                            1,
                                                            record
                                                        )
                                                "
                                            >
                                                <a-button
                                                    type="text"
                                                    size="small"
                                                    class="!p-1 !h-6 !w-6 !min-w-6"
                                                    title="刪除"
                                                >
                                                    <span
                                                        class="text-red-500 text-sm"
                                                    >
                                                        ✕
                                                    </span>
                                                </a-button>
                                            </a-popconfirm>
                                        </a-space>
                                    </template>
                                </a-table-column>
                            </a-table>
                        </a-card>
                    </div>

                    <!-- 負債類型統計表格 -->
                    <div v-show="!liabilityDetailsCollapsed" class="mt-4">
                        <a-card size="small">
                            <template #title>
                                <div class="text-gray-500">負債類型統計</div>
                            </template>
                            <a-table
                                :data-source="liabilityGroups"
                                :pagination="false"
                                row-key="type_name"
                                size="small"
                                :row-class-name="() => ''"
                            >
                                <a-table-column
                                    title="類型"
                                    dataIndex="type_name"
                                    key="type_name"
                                >
                                    <template #default="{ record }">
                                        <span class="font-medium text-gray-800">
                                            {{ record.type_name }}
                                        </span>
                                    </template>
                                </a-table-column>
                                <a-table-column
                                    title="總金額"
                                    key="total_amount"
                                >
                                    <template #default="{ record }">
                                        <span
                                            class="font-semibold text-red-600"
                                        >
                                            {{
                                                formatCurrency(
                                                    record.total_amount
                                                )
                                            }}
                                        </span>
                                    </template>
                                </a-table-column>
                                <a-table-column
                                    title="月支出合計"
                                    key="total_monthly_interest_expense"
                                >
                                    <template #default="{ record }">
                                        <span
                                            class="font-semibold text-red-600"
                                        >
                                            {{
                                                formatCurrency(
                                                    record.total_monthly_interest_expense
                                                )
                                            }}
                                        </span>
                                    </template>
                                </a-table-column>
                            </a-table>
                        </a-card>
                    </div>
                </a-card>
            </div>


            <div class="mb-4" v-if="!isReadonly && versionSource === 0">
                <a-switch v-model:checked="versionInfoForm.requires_booking" />
                <span class="ml-2">需預約</span>
            </div>
            <div class="mt-2 flex flex-col gap-4">
                <a-button
                    v-if="!isReadonly"
                    @click="isAdminFlatEditor ? saveFlatSheetFromSummary() : showFinishConfirm()"
                    size="large"
                    block
                    class="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-white"
                    :loading="finishLoading"
                >
                    <template #icon>
                        <CheckCircleOutlined />
                    </template>
                    <span class="font-semibold text-white">{{
                        isAdminFlatEditor
                            ? (isConsultationMode ? (Number(versionSource) === 2 ? '儲存諮詢版' : '儲存盤點版') : '儲存草稿')
                            : (isConsultationMode ? (Number(versionSource) === 2 ? '完成諮詢版' : '完成盤點版') : '完成資產負債表')
                    }}</span>
                </a-button>
                <a-button
                    v-if="!isReadonly && isAdminFlatEditor && isConsultationMode"
                    @click="handleFinish"
                    size="large"
                    block
                    type="primary"
                    :loading="finishLoading"
                >
                    <CheckCircleOutlined />
                    完成版本
                </a-button>
                <a-button v-else type="primary" block @click="goBack">
                    返回列表
                </a-button>
            </div>
        </a-card>
        </template>

        <!-- 新增/編輯 資產/負債 彈窗 -->
        <a-modal
            :open="modalVisible && !isAdminFlatEditor"
            :confirm-loading="modalLoading"
            :title="modalMode === 'add' ? '新增' : '編輯'"
            @ok="submitModal"
            @cancel="closeModal"
            :mask-closable="true"
            :width="720"
            :ok-button-props="{ disabled: isReadonly }"
            ok-text="確認"
            cancel-text="取消"
            wrap-class-name="edit-modal"
            :centered="true"
        >
            <a-form layout="vertical" :disabled="isReadonly">
                <a-alert
                    v-if="!modalTypeId"
                    type="warning"
                    message="請先選取類型"
                    show-icon
                    class="mb-2"
                />
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <a-form-item label="選項" :required="true">
                        <a-select
                            v-model:value="modalForm.option_id"
                            :loading="optionLoading"
                            :disabled="!modalTypeId"
                            @change="handleOptionChange"
                        >
                            <a-select-option
                                v-for="opt in optionList"
                                :key="opt.value"
                                :value="opt.value"
                            >
                                {{ opt.label }}
                            </a-select-option>
                        </a-select>
                        <div
                            v-if="selectedOptionMeta && selectedOptionMeta.hint"
                            class="text-xs text-gray-500 mt-1"
                        >
                            {{ selectedOptionMeta.hint }}
                        </div>
                    </a-form-item>
                    <a-form-item label="名稱" :required="true">
                        <a-input v-model:value="modalForm.name" />
                    </a-form-item>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <a-form-item
                        :label="modalClassType === 0 ? '市值(現值)' : '餘額'"
                        :required="amountIsRequired"
                    >
                        <a-input-number
                            v-model:value="modalForm.amount"
                            :min="0"
                            style="width: 100%"
                        />
                    </a-form-item>
                    <a-form-item
                        :label="modalClassType === 0 ? '月收入' : '月付金'"
                        :required="cashFlowIsRequired"
                    >
                        <a-input-number
                            v-model:value="modalForm.monthly_interest_expense"
                            :min="0"
                            style="width: 100%"
                        />
                    </a-form-item>
                </div>
                <template v-if="modalFields.length > 0">
                    <a-divider>
                        請填寫以下詳細資訊 ({{ modalFields.length }} 項)
                    </a-divider>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div
                            v-for="f in modalFields"
                            :key="f.id"
                            v-show="shouldShowField(f)"
                        >
                            <a-form-item
                                :label="f.name || '字段#' + f.id"
                                :required="true"
                            >
                                <template v-if="f.input_type === 'number'">
                                    <a-input-number
                                        v-model:value="modalFieldValues[f.id]"
                                        style="width: 180px"
                                    />
                                </template>
                                <template
                                    v-else-if="
                                        f.input_type === 'array' ||
                                        f.type === 'array'
                                    "
                                >
                                    <a-select
                                        :options="getFieldOptions(f)"
                                        :loading="getFieldLoading(f)"
                                        :value="getArraySingleValue(f.id)"
                                        style="width: 100%"
                                        @dropdownVisibleChange="
                                            (open) =>
                                                handleArrayDropdown(open, f)
                                        "
                                        @change="
                                            (val) =>
                                                setArraySingleValue(f.id, val)
                                        "
                                        placeholder="請選擇"
                                    />
                                </template>
                                <template
                                    v-else-if="
                                        f.type === 'boolean' ||
                                        f.input_type === 'boolean'
                                    "
                                >
                                    <a-select
                                        v-model:value="modalFieldValues[f.id]"
                                        style="width: 120px"
                                    >
                                        <a-select-option :value="1">
                                            是
                                        </a-select-option>
                                        <a-select-option :value="0">
                                            否
                                        </a-select-option>
                                    </a-select>
                                </template>
                                <template v-else>
                                    <a-input
                                        v-model:value="modalFieldValues[f.id]"
                                    />
                                </template>
                                <div
                                    v-if="f.hint"
                                    class="text-xs text-gray-500 mt-1"
                                >
                                    {{ f.hint }}
                                </div>
                            </a-form-item>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <a-divider>請填寫以下詳細資訊 (0 項)</a-divider>
                    <div class="text-gray-500 text-center py-4">無額外資訊</div>
                </template>
            </a-form>
        </a-modal>

        <!-- 陣列字段子彈窗 -->
        <a-modal
            :open="arrayModalVisible"
            :confirm-loading="arrayLoading"
            title="選擇項目"
            wrap-class-name="edit-modal"
            @ok="confirmArrayModal"
            @cancel="() => (arrayModalVisible = false)"
            :mask-closable="true"
            ok-text="確認"
            cancel-text="取消"
        >
            <a-select
                v-model:value="arraySelected"
                mode="multiple"
                style="width: 100%"
                :options="arrayChoices"
            />
        </a-modal>

        <!-- 總結頁面類型選擇對話框 -->
        <a-modal
            v-model:open="summaryTypeModalVisible"
            :title="
                summaryTypeModalClass === 0 ? '選擇資產類型' : '選擇負債類型'
            "
            wrap-class-name="edit-modal"
            @ok="confirmSummaryTypeSelection"
            @cancel="() => (summaryTypeModalVisible = false)"
            :mask-closable="true"
            ok-text="確認"
            cancel-text="取消"
        >
            <div class="py-4">
                <div class="mb-3">
                    請選擇要新增的{{
                        summaryTypeModalClass === 0 ? '資產' : '負債'
                    }}類型：
                </div>
                <a-select
                    v-model:value="summarySelectedTypeId"
                    :options="summaryTypeOptions"
                    placeholder="請選擇類型"
                    style="width: 100%"
                />
            </div>
        </a-modal>

        <VersionItemViewModal
            v-model:open="viewItemVisible"
            :record="viewItemRecord"
        />
    </div>
</template>

<style scoped>
.consultant-one-page-editor {
    max-width: 1200px;
    margin: 0 auto;
}
.consultant-one-page-editor .consultant-section-card {
    margin-bottom: 20px;
}
.consultant-one-page-editor .consultant-section-card :deep(.ant-card-head-title) {
    font-weight: 600;
}
.flat-sheet-inline-form :deep(.ant-form-item) {
    margin-bottom: 0;
}
.admin-editor-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
    padding: 0 2px 10px;
    border-bottom: 1px solid #e2e8f0;
}
.admin-editor-topbar__nav {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex-wrap: wrap;
}
.admin-editor-topbar__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #8b6914;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.admin-editor-topbar__back:hover {
    color: #6b5428;
}
.admin-editor-topbar__sep {
    color: #cbd5e1;
    font-size: 12px;
}
.admin-editor-topbar__title {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
}
.admin-editor-topbar__tag {
    margin: 0;
    border-radius: 999px;
}
.admin-editor-topbar__booking {
    max-width: min(420px, 100%);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
    padding: 2px 10px;
    border: 0;
    border-radius: 999px;
    background: #ecfdf5;
    color: #0f766e;
    font: inherit;
    font-size: 12px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    text-align: left;
}
.admin-editor-topbar__booking:hover {
    background: #d1fae5;
    text-decoration: underline;
    text-underline-offset: 2px;
}
.admin-editor-topbar__booking:focus-visible {
    outline: 2px solid #99f6e4;
    outline-offset: 2px;
}
.flat-sheet-input-grid {
    overflow: hidden;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    background: #fff;
}
.flat-sheet-input-grid :deep(.ant-form-item) {
    padding: 6px 8px 8px;
    border-right: 1px solid #d0d5dd;
}
.flat-sheet-input-grid :deep(.ant-form-item:last-child) {
    border-right: 0;
}
.flat-sheet-input-grid :deep(.ant-form-item-label) {
    padding-bottom: 3px;
}
.flat-sheet-input-grid :deep(.ant-form-item-label > label) {
    height: auto;
    color: #475467;
    font-size: 11px;
    font-weight: 700;
}
.flat-sheet-input-grid :deep(.ant-input),
.flat-sheet-input-grid :deep(.ant-input-number) {
    border: 0;
    border-radius: 0;
    box-shadow: none;
}
.admin-version-fields,
.admin-personal-fields {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
.admin-personal-fields > div {
    display: contents;
}
.admin-version-fields :deep(.ant-form-item),
.admin-personal-fields :deep(.ant-form-item) {
    margin-bottom: 0;
}
.admin-personal-fields :deep(.ant-input-number),
.admin-personal-fields :deep(.ant-select) {
    width: 100% !important;
}
@media (max-width: 767px) {
    .admin-version-fields,
    .admin-personal-fields {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .flat-sheet-input-grid :deep(.ant-form-item) {
        border-right: 0;
        border-bottom: 1px solid #d0d5dd;
    }
    .flat-sheet-input-grid :deep(.ant-form-item:last-child) {
        border-bottom: 0;
    }
}
@media (max-width: 480px) {
    .admin-version-fields,
    .admin-personal-fields {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
